import React, {useContext, useEffect, useRef, useState} from "react";
import {t} from "i18next";
import {Header} from "../components/Header";
import {countryCodes} from "../data/CountryCode";
import {AccountContext} from "../context/AccountProvider";
import {familyTreeNames} from "../data/FamilyTreeNames";
import {getFilteredUsers} from "../service/accountApi";
import useDebounce from "../hooks/useDebounce"
import {Spinner} from "../components/Spinner.tsx";
import {ChevronDownIcon, DownloadIcon} from "@radix-ui/react-icons";
import {Transition} from "@headlessui/react";
import _ from "lodash";
import {FlyoutFiltersSection} from "../components/FlyoutFiltersSection";
import {toast} from "react-toastify";
import {Modal} from "../components/Modal";
import Cookies from "universal-cookie";
import {useAxiosPrivate} from "../hooks/useAxiosPrivate";
import {generateExcelFile} from "../service/admin/fileApi";
import LZString from 'lz-string';

export const AdvancedSearchPage = () => {
    const {account, isAuthenticated, isAdmin} = useContext(AccountContext);

    // Hooks
    const axiosPrivate = useAxiosPrivate();

    const cookies = new Cookies();
    const locale = cookies.get("locale");

    // Filter Type Variables
    const [isBasicFiltersTabSelected, setIsBasicFiltersTabSelected] = useState(true);
    const [isQuickAccessFiltersTabSelected, setIsQuickAccessFiltersTabSelected] = useState(false);

    // Family Tree Name Variables
    const familyTreeNameSearchInputRef = useRef();
    const [isFamilyTreeNameSearchInputBlurred, setIsFamilyTreeNameSearchInputBlurred] = useState(false);

    const [filteredFamilyTreeNames, setFilteredFamilyTreeNames] = useState(familyTreeNames);
    const [isFamilyTreeNameDropdownOpen, setIsFamilyTreeNameDropdownOpen] = useState(false);
    const [isSelectingFamilyTreeName, setIsSelectingFamilyTreeName] = useState(false);

    const [selectedFamilyTreeName, setSelectedFamilyTreeName] = useState("");

    // Date Of Birth Variables
    const dobFilterByTypeOptions = [
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.DEFAULT"),
            value: "",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.AGE_RANGE"),
            value: "age-range",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.DATE"),
            value: "date",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.MONTH"),
            value: "month",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.MONTH_YEAR"),
            value: "month-year",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.YEAR"),
            value: "year"
        },
    ];

    const [isDOBFilterByTypeDisabled, setIsDOBFilterByTypeDisabled] = useState(false);

    const [isDOBFilterByTypeDropdownOpen, setIsDOBFilterByTypeDropdownOpen] = useState(false);
    const [isSelectingDOBFilterByType, setIsSelectingDOBFilterByType] = useState(false);

    const [selectedDOBFilterByType, setSelectedDOBFilterByType] = useState("");

    // Age-Range
    const [selectedMinAge, setSelectedMinAge] = useState("");
    const [selectedMaxAge, setSelectedMaxAge] = useState("");

    // Date
    const [selectedDateOfBirth, setSelectedDateOfBirth] = useState("");

    // Month
    const monthsList = [
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.DEFAULT"),
            value: "",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JANUARY"),
            value: "1",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.FEBRUARY"),
            value: "2",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.MARCH"),
            value: "3",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.APRIL"),
            value: "4",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.MAY"),
            value: "5",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JUNE"),
            value: "6",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JULY"),
            value: "7",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.AUGUST"),
            value: "8",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.SEPTEMBER"),
            value: "9",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.OCTOBER"),
            value: "10",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.NOVEMBER"),
            value: "11",
        },
        {
            label: t("SEARCH_PAGE.FILTERS.DOB.MONTHS.DECEMBER"),
            value: "12",
        },
    ]

    const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
    const [isSelectingMonth, setIsSelectingMonth] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState("");

    // Month-Year
    const [selectedMonthYear, setSelectedMonthYear] = useState("");

    // Year
    const [selectedYear, setSelectedYear] = useState("");

    // Gender Variables
    const [isGenderDisabled, setIsGenderDisabled] = useState(false);

    const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
    const [isSelectingGender, setIsSelectingGender] = useState(false);

    const [selectedGender, setSelectedGender] = useState("");

    // Blood Group Variables
    const bloodGroups = [t("SEARCH_PAGE.FILTERS.BLOOD_GROUP.DEFAULT"), "A +", "A -", "B +", "B -", "O +", "O -", "AB +", "AB -"];

    const [isBloodGroupDropdownOpen, setIsBloodGroupDropdownOpen] = useState(false);
    const [isSelectingBloodGroup, setIsSelectingBloodGroup] = useState(false);

    const [selectedBloodGroup, setSelectedBloodGroup] = useState("");

    // Marital Status Variables
    const maritalStatusOptionsList = [
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DEFAULT"),
            value: ""
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MINOR"),
            value: "Minor"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.SINGLE"),
            value: "Single"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MARRIED"),
            value: "Married"
        },
        {
            label: selectedGender === "male"
                ? t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWER")
                : t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWED"),
            value: selectedGender === "male" ? "Widowed" : "Widowed"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DIVORCED"),
            value: "Divorced"
        }
    ];
    const sasurVashinMaritalStatusOptionsList = [
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DEFAULT"),
            value: ""
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MARRIED"),
            value: "Married"
        },
        {
            label: selectedGender === "male"
                ? t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWER")
                : t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWED"),
            value: selectedGender === "male" ? "Widowed" : "Widowed"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DIVORCED"),
            value: "Divorced"
        }
    ];
    const maherVashinMaritalStatusOptionsList = [
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DEFAULT"),
            value: ""
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MINOR"),
            value: "Minor"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.SINGLE"),
            value: "Single"
        }
    ];

    const [maritalStatusOptions, setMaritalStatusOptions] = useState([
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DEFAULT"),
            value: ""
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MINOR"),
            value: "Minor"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.SINGLE"),
            value: "Single"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MARRIED"),
            value: "Married"
        },
        {
            label: selectedGender === "male"
                ? t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWER")
                : t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWED"),
            value: selectedGender === "male" ? "Widowed" : "Widowed"
        },
        {
            label: t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DIVORCED"),
            value: "Divorced"
        }
    ]);

    const [isMaritalStatusDisabled, setIsMaritalStatusDisabled] = useState(false);

    const [isMaritalStatusDropdownOpen, setIsMaritalStatusDropdownOpen] = useState(false);
    const [isSelectingMaritalStatus, setIsSelectingMaritalStatus] = useState(false);

    const [selectedMaritalStatus, setSelectedMaritalStatus] = useState("");

    // Pincode Variables
    const [isPincodeInputFocused, setIsPincodeInputFocused] = useState(false);
    const [pincode, setPincode] = useState("");

    // City Variables
    const [isCityInputFocused, setIsCityInputFocused] = useState(false);
    const [city, setCity] = useState("");

    // State Variables
    const [state, setState] = useState("");
    const [isStateInputFocused, setIsStateInputFocused] = useState(false);

    // Country Variables
    const [filteredCountryDetails, setFilteredCountryDetails] = useState(countryCodes);

    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [isSelectingCountry, setIsSelectingCountry] = useState(false);

    const [selectedCountry, setSelectedCountry] = useState("");

    // Phone Number Variables
    const [isCountryCodeDropdownOpen, setIsCountryCodeDropdownOpen] = useState(false);
    const [isSelectingCountryCode, setIsSelectingCountryCode] = useState(false);

    const [phoneNumberDetails, setPhoneNumberDetails] = useState({
        charCode: 'IN',
        dialingCode: '+91',
        countryName: 'India',
        number: '',
    });

    // Email Variables
    const [isEmailInputFocused, setIsEmailInputFocused] = useState(false);
    const [email, setEmail] = useState("");

    // Quick Access Variables
    const [isUnmarriedMenSelected, setIsUnmarriedMenSelected] = useState(false);
    const [isUnmarriedWomenSelected, setIsUnmarriedWomenSelected] = useState(false);
    const [isSasurVashinSelected, setIsSasurVashinSelected] = useState(false);
    const [isMaherVashinSelected, setIsMaherVashinSelected] = useState(false);
    const [isAdoptedSelected, setIsAdoptedSelected] = useState(false);
    const [isShatayushiVyaktiSelected, setIsShatayushiVyaktiSelected] = useState(false);
    const [isDirghayushiVyaktiSelected, setIsDirghayushiVyaktiSelected] = useState(false);

    // Show People Living | Deceased Variables
    const [showAllPeople, setShowAllPeople] = useState(true);
    const [isAllPeopleLivingAndDeceasedSelected, setIsAllPeopleLivingAndDeceasedSelected] = useState(true);
    const [isLivingPeopleOnlySelected, setIsLivingPeopleOnlySelected] = useState(false);

    // Filters Data
    const [isUpdatingFilters, setIsUpdatingFilters] = useState(false);
    const [existingFilters, setExistingFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({});

    // Filter Button Variables
    const [isClearFiltersButtonDisabled, setIsClearFiltersButtonDisabled] = useState(true);
    const [isApplyFiltersButtonDisabled, setIsApplyFiltersButtonDisabled] = useState(true);

    // Previous Filters
    const [previousFilters, setPreviousFilters] = useState({
        selectedFamilyTreeName, selectedDOBFilterByType, selectedMinAge, selectedMaxAge,
        selectedDateOfBirth, selectedMonth, selectedMonthYear, selectedYear,
        selectedGender, selectedBloodGroup, selectedMaritalStatus, pincode,
        city, state, selectedCountry, phoneNumberDetails,
        email, isUnmarriedMenSelected, isUnmarriedWomenSelected, isSasurVashinSelected,
        isMaherVashinSelected, isAdoptedSelected, isShatayushiVyaktiSelected, isDirghayushiVyaktiSelected
    });

    // Search Bar Variables
    const searchInputRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [searchResults, setSearchResults] = useState([]);
    // const [cachedSearchResults, setCachedSearchResults] = useState(JSON.parse(LZString.decompressFromUTF16(localStorage.getItem("cachedSearchResults"))) || {});

    const [cachedSearchResults, setCachedSearchResults] = useState(() => {
        const cachedSearchData = localStorage.getItem("cachedSearchResults");

        if (cachedSearchData) {
            try {
                const decompressed = LZString.decompressFromUTF16(cachedSearchData);
                if (decompressed) {
                    return JSON.parse(decompressed);
                }
            } catch (e) {
                console.error("Failed to decompress or parse cached search results:", e);
                toast.error("Failed to load cached search results. Please report to Admin!");
            }
        }

        return {}; // fallback default
    });

    const [filteredSearchResults, setFilteredSearchResults] = useState([]);
    const [isSearchResultsLoading, setIsSearchResultsLoading] = useState(true);
    const [searchResultsCount, setSearchResultsCount] = useState(null);

    // Export as Excel Variables
    const [isExportAsExcelDisabled, setIsExportAsExcelDisabled] = useState(true);
    const [isExportAsExcelLoading, setIsExportAsExcelLoading] = useState(false);

    const searchResultsExcelHeader = {
        "_id": "ID",
        "firstName": "First Name",
        "middleName": "Middle Name",
        "phoneNumber": "Phone Number",
        "emailId": "Email ID",
        "familyTreeName": "Family Tree Name",
    };

    // Selected Profile
    const [showSelectedProfileDetailsModal, setShowSelectedProfileDetailsModal] = useState(false);
    const [selectedProfileDetails, setSelectedProfileDetails] = useState(null);

    // Filter Type Functions
    const handleFilterTypeTabSelect = (tab) => {
        if (tab === "basic") {
            setIsBasicFiltersTabSelected(true);
            setIsQuickAccessFiltersTabSelected(false);
        }

        if (tab === "quick-access") {
            setIsBasicFiltersTabSelected(false);
            setIsQuickAccessFiltersTabSelected(true);
        }
    }

    // Family Tree Name Functions
    const handleFamilyTreeNameSelect = (familyTreeName) => {
        setIsSelectingFamilyTreeName(false);
        setIsFamilyTreeNameDropdownOpen(false);

        if (familyTreeName !== selectedFamilyTreeName) {
            setSelectedFamilyTreeName(familyTreeName);

            // if (familyTreeName === "") {
            //     delete existingFilters.familyTreeName;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         familyTreeName: familyTreeName
            //     });
            // }
        }
    }

    useEffect(() => {
        if (isFamilyTreeNameDropdownOpen) {
            familyTreeNameSearchInputRef.current?.focus();
        }
    }, [isFamilyTreeNameDropdownOpen]);

    useEffect(() => {
        if (isFamilyTreeNameSearchInputBlurred) {
            setTimeout(() => {
                setIsFamilyTreeNameSearchInputBlurred(false);
            }, 100);
        }
    }, [isFamilyTreeNameSearchInputBlurred]);

    // D.O.B Functions
    const dobFilterByTypeLabel = (dobFilterByType) => {
        switch (dobFilterByType) {
            case 'age-range':
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.AGE_RANGE");
            case 'date':
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.DATE");
            case 'month':
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.MONTH");
            case 'month-year':
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.MONTH_YEAR");
            case 'year':
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.YEAR");
            default:
                return t("SEARCH_PAGE.FILTERS.DOB.FILTER_BY_TYPE.DEFAULT");
        }
    }

    const handleDOBFilterByTypeSelect = (dobFilterByType) => {
        setIsSelectingDOBFilterByType(false);
        setIsDOBFilterByTypeDropdownOpen(false);

        if (dobFilterByType !== selectedDOBFilterByType) {
            setSelectedDOBFilterByType(dobFilterByType);
            setSelectedMinAge("");
            setSelectedMaxAge("");
            setSelectedDateOfBirth("");
            setSelectedMonth("");
            setSelectedMonthYear("");
            setSelectedYear("");

            // if (dobFilterByType === "") {
            //     delete existingFilters.dobFilterByType;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         dobFilterByType: dobFilterByType
            //     });
            // }
            //
            // delete existingFilters.minAge;
            // delete existingFilters.maxAge;
            // delete existingFilters.dateOfBirth;
        }
    }

    const monthLabel = (month) => {
        switch (month) {
            case "1":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JANUARY");
            case "2":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.FEBRUARY");
            case "3":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.MARCH");
            case "4":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.APRIL");
            case "5":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.MAY");
            case "6":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JUNE");
            case "7":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.JULY");
            case "8":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.AUGUST");
            case "9":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.SEPTEMBER");
            case "10":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.OCTOBER");
            case "11":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.NOVEMBER");
            case "12":
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.DECEMBER");
            default:
                return t("SEARCH_PAGE.FILTERS.DOB.MONTHS.DEFAULT");
        }
    }

    const handleMonthSelect = (month) => {
        setIsSelectingMonth(false);
        setIsMonthDropdownOpen(false);
        setSelectedMonth(month);

        if (month !== selectedMonth) {
            setSelectedMonth(month);

            // if (month === "") {
            //     delete existingFilters.dateOfBirth;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         dateOfBirth: month
            //     });
            // }
        }
    }

    // Gender Functions
    const handleGenderSelect = (gender) => {
        setIsSelectingGender(false);
        setIsGenderDropdownOpen(false);
        setSelectedGender(gender);

        if (gender !== selectedGender) {
            setSelectedGender(gender);

            // if (gender === "") {
            //     delete existingFilters.gender;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         gender: gender
            //     });
            // }
        }
    }

    const genderLabel = (gender) => {
        switch (gender) {
            case "male":
                return {
                    label: t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.MALE"),
                    icon: "/assets/male-gender-icon-bold.png"
                }
            case "female":
                return {
                    label: t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.FEMALE"),
                    icon: "/assets/female-gender-icon-bold.png"
                }
            default:
                return {
                    label: t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.DEFAULT"),
                }
        }
    }

    // Blood Group Functions
    const handleBloodGroupSelect = (bloodGroup) => {
        setIsSelectingBloodGroup(false);
        setIsBloodGroupDropdownOpen(false);

        if (bloodGroup !== selectedBloodGroup) {
            setSelectedBloodGroup(bloodGroup);

            // if (bloodGroup === "") {
            //     delete existingFilters.bloodGroup;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         bloodGroup: bloodGroup
            //     });
            // }
        }
    }

    // Marital Status Functions
    const maritalStatusLabel = (maritalStatus) => {
        switch (maritalStatus) {
            case 'Single':
                return t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.SINGLE");
            case 'Married':
                return t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MARRIED");
            case 'Widowed':
                return selectedGender === "male"
                    ? t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWER")
                    : t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.WIDOWED");
            case 'Divorced':
                return t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DIVORCED");
            case 'Minor':
                return t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.MINOR");
            default:
                return t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.DEFAULT");
        }
    }

    const handleMaritalStatusSelect = (maritalStatus) => {
        setIsSelectingMaritalStatus(false);
        setIsMaritalStatusDropdownOpen(false);

        if (maritalStatus !== selectedMaritalStatus) {
            setSelectedMaritalStatus(maritalStatus);

            // if (maritalStatus === "") {
            //     delete existingFilters.maritalStatus;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         maritalStatus: maritalStatus
            //     });
            // }
        }
    }

    // Pin Code City, State. Country Functions
    const handlePincodeChange = (e) => {
        if (e.target.value !== pincode) {
            setPincode(e.target.value);

            // if (e.target.value === "") {
            //     delete existingFilters.pincode;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         pincode: e.target.value
            //     })
            // }
        }
    }

    const handleCityChange = (e) => {
        if (e.target.value !== city) {
            setCity(e.target.value);

            // if (e.target.value === "") {
            //     delete existingFilters.city;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         city: e.target.value
            //     })
            // }
        }
    }

    const handleStateChange = (e) => {
        if (e.target.value !== state) {
            setState(e.target.value);

            // if (e.target.value === "") {
            //     delete existingFilters.state;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         state: e.target.value
            //     })
            // }
        }
    }

    const handleCountrySelect = (country) => {
        setIsSelectingCountry(false);
        setIsCountryDropdownOpen(false);

        if (country !== selectedCountry) {
            setSelectedCountry(country);

            // if (country === "") {
            //     delete existingFilters.country;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         country: country
            //     });
            // }
        }
    }

    // Phone Number Functions
    const handleCountryCodeSelect = (countryDetails) => {
        setIsSelectingCountryCode(false);
        setIsCountryCodeDropdownOpen(false);

        if (countryDetails.charCode !== phoneNumberDetails.charCode) {
            setPhoneNumberDetails({
                ...phoneNumberDetails,
                charCode: countryDetails.countryCode,
                dialingCode: countryDetails.countryDialingCode,
                countryName: countryDetails.countryName,
            });

            // setExistingFilters({
            //     ...existingFilters,
            //     phoneNumber: {
            //         ...existingFilters.phoneNumber,
            //         charCode: countryDetails.countryCode,
            //         dialingCode: countryDetails.countryDialingCode,
            //         countryName: countryDetails.countryName,
            //     }
            // })
        }
    }

    const handlePhoneNumberChange = (e) => {
        if (e.target.value !== phoneNumberDetails.number) {
            setPhoneNumberDetails({
                ...phoneNumberDetails,
                number: e.target.value
            });

            // if (e.target.value === "") {
            //     delete existingFilters.phoneNumber;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         phoneNumber: {
            //             ...existingFilters.phoneNumber,
            //             number: e.target.value
            //         }
            //     });
            // }
        }
    }

    // Email Functions
    const handleEmailChange = (e) => {
        if (e.target.value !== email) {
            setEmail(e.target.value);

            // if (e.target.value === "") {
            //     delete existingFilters.email;
            // } else {
            //     setExistingFilters({
            //         ...existingFilters,
            //         email: e.target.value
            //     });
            // }
        }
    }

    // Quick Access Functions
    const handleUnmarriedMenSelect = (e) => {
        setIsUnmarriedMenSelected(e.target.checked);

        if (e.target.checked) {
            setIsMaritalStatusDisabled(true);
            setIsGenderDisabled(true);

            setSelectedMaritalStatus("Single");

            if (isUnmarriedWomenSelected) {
                setSelectedGender("");
            } else {
                setSelectedGender("male");
            }
        } else {
            if (!isUnmarriedWomenSelected) {
                setIsMaritalStatusDisabled(false);
                setIsGenderDisabled(false);

                setSelectedGender("");
                setSelectedMaritalStatus("");
            } else {
                setSelectedGender("female");
                setSelectedMaritalStatus("Single");
            }

            // delete existingFilters.unMarriedMen;
        }
    }

    const handleUnmarriedWomenSelect = (e) => {
        setIsUnmarriedWomenSelected(e.target.checked);

        if (e.target.checked) {
            setIsMaritalStatusDisabled(true);
            setIsGenderDisabled(true);

            setSelectedMaritalStatus("Single");

            if (isUnmarriedMenSelected) {
                setSelectedGender("");
            } else {
                setSelectedGender("female");
            }
        } else {
            if (!isUnmarriedMenSelected) {
                setIsMaritalStatusDisabled(false);
                setIsGenderDisabled(false);

                setSelectedGender("");
                setSelectedMaritalStatus("");
            } else {
                setSelectedGender("male");
                setSelectedMaritalStatus("Single");
            }
        }
    }

    const handleSasurVashinSelect = (e) => {
        setIsSasurVashinSelected(e.target.checked);

        if (e.target.checked) {
            setIsGenderDisabled(true);
            setSelectedGender("female");

            if (isMaherVashinSelected) {
                setIsMaritalStatusDisabled(true);
                setSelectedMaritalStatus("");
                setMaritalStatusOptions(maritalStatusOptionsList);
            } else {
                setIsMaritalStatusDisabled(false);
                setSelectedMaritalStatus("Married");
                setMaritalStatusOptions(sasurVashinMaritalStatusOptionsList);
            }
        } else {
            if (isMaherVashinSelected) {
                setIsGenderDisabled(true);
                setIsMaritalStatusDisabled(false);
                setSelectedGender("female");
                setSelectedMaritalStatus("Single");
                setMaritalStatusOptions(maherVashinMaritalStatusOptionsList);
            } else {
                setIsGenderDisabled(false);
                setIsMaritalStatusDisabled(false);
                setSelectedGender("");
                setSelectedMaritalStatus("");
                setMaritalStatusOptions(maritalStatusOptionsList);
            }
        }
    }

    const handleMaherVashinSelect = (e) => {
        setIsMaherVashinSelected(e.target.checked);

        if (e.target.checked) {
            setIsGenderDisabled(true);
            setSelectedGender("female");

            if (isSasurVashinSelected) {
                setIsMaritalStatusDisabled(true);
                setSelectedMaritalStatus("");
                setMaritalStatusOptions(maritalStatusOptionsList);
            } else {
                setIsMaritalStatusDisabled(true);
                setSelectedMaritalStatus("Single");
                setMaritalStatusOptions(sasurVashinMaritalStatusOptionsList);
            }
        } else {
            if (isSasurVashinSelected) {
                setIsGenderDisabled(true);
                setIsMaritalStatusDisabled(false);
                setSelectedGender("female");
                setSelectedMaritalStatus("Married");
                setMaritalStatusOptions(sasurVashinMaritalStatusOptionsList);
            } else {
                setIsGenderDisabled(false);
                setIsMaritalStatusDisabled(false);
                setSelectedGender("");
                setSelectedMaritalStatus("");
                setMaritalStatusOptions(maritalStatusOptionsList);
            }
        }
    }

    const handleShatayushiVyaktiSelect = (e) => {
        setIsShatayushiVyaktiSelected(e.target.checked);

        if (e.target.checked) {
            setIsDOBFilterByTypeDisabled(true);
        } else {
            if (!isDirghayushiVyaktiSelected) {
                setIsDOBFilterByTypeDisabled(false);
            }
        }
    }

    const handleDirghayushiVyaktiSelect = (e) => {
        setIsDirghayushiVyaktiSelected(e.target.checked);

        if (e.target.checked) {
            setIsDOBFilterByTypeDisabled(true);
            // setExistingFilters({
            //     ...existingFilters,
            //     dirghayushiVyakti: true
            // });
        } else {
            if (!isShatayushiVyaktiSelected) {
                setIsDOBFilterByTypeDisabled(false);
            }

            // delete existingFilters.dirghayushiVyakti;
        }
    }

    // Show People (Living | Deceased) Functions
    // const handleShowAllPeopleLivingAndDeceasedSelect = (e) => {
    //     setIsAllPeopleLivingAndDeceasedSelected(true);
    //     setIsLivingPeopleOnlySelected(false);
    // }
    //
    // const handleShowLivingPeopleOnlySelect = (e) => {
    //     setIsAllPeopleLivingAndDeceasedSelected(false);
    //     setIsLivingPeopleOnlySelected(true);
    // }

    // Search Related Functions
    const handleSearchQueryClearButtonClick = () => {
        setIsSearchResultsLoading(true);
        if (Object.keys(selectedFilters).length === 0) {
            setSearchResultsCount(null);
        }

        setSearchQuery("");
        setSearchResults([]);
        searchInputRef.current?.focus();
    }

    const handleClearFilters = () => {
        setIsUpdatingFilters(false);

        setSelectedFamilyTreeName("");
        setSelectedDOBFilterByType("");
        setSelectedMinAge("");
        setSelectedMaxAge("");
        setSelectedDateOfBirth("");
        setSelectedMonth("");
        setSelectedMonthYear("");
        setSelectedYear("");
        setSelectedGender("");
        setSelectedBloodGroup("");
        setSelectedMaritalStatus("");
        setPincode("");
        setCity("");
        setState("");
        setSelectedCountry("");
        setPhoneNumberDetails({
            charCode: 'IN',
            dialingCode: '+91',
            countryName: 'India',
            number: '',
        });
        setEmail("");
        setIsUnmarriedMenSelected(false);
        setIsUnmarriedWomenSelected(false);
        setIsSasurVashinSelected(false);
        setIsMaherVashinSelected(false);
        setIsAdoptedSelected(false);
        setIsShatayushiVyaktiSelected(false);
        setIsDirghayushiVyaktiSelected(false);

        setShowAllPeople(true);
        // setIsAllPeopleLivingAndDeceasedSelected(true);
        // setIsLivingPeopleOnlySelected(false);

        // setExistingFilters({});
        setSelectedFilters({});
    }

    const handleApplyFilters = async () => {
        setIsSearchResultsLoading(true);
        console.log("Inside Apply Filters!");

        setIsUpdatingFilters(false);
        setIsApplyFiltersButtonDisabled(true);
        setIsClearFiltersButtonDisabled(true);

        const filtersData = {};

        if (showAllPeople) {
            filtersData.showAllPeople = true;
        }

        if (!showAllPeople) {
            filtersData.showAllPeople = false;
        }

        if (selectedFamilyTreeName !== "") {
            filtersData.familyTreeName = selectedFamilyTreeName;
        } else {
            delete filtersData.familyTreeName;
        }

        if (selectedDOBFilterByType !== "") {
            filtersData.dobFilterByType = selectedDOBFilterByType;

            if (selectedDOBFilterByType === "age-range") {
                filtersData.minAge = selectedMinAge;
                filtersData.maxAge = selectedMaxAge;
                delete filtersData.dateOfBirth;
            }

            if (selectedDOBFilterByType === "date") {
                filtersData.dateOfBirth = selectedDateOfBirth;
                delete filtersData.minAge;
                delete filtersData.maxAge;
            }

            if (selectedDOBFilterByType === "month") {
                filtersData.dateOfBirth = selectedMonth;
                delete filtersData.minAge;
                delete filtersData.maxAge;
            }

            if (selectedDOBFilterByType === "month-year") {
                filtersData.dateOfBirth = selectedMonthYear;
                delete filtersData.minAge;
                delete filtersData.maxAge;
            }

            if (selectedDOBFilterByType === "year") {
                filtersData.dateOfBirth = selectedYear;
                delete filtersData.minAge;
                delete filtersData.maxAge;
            }
        } else {
            delete filtersData.dobFilterByType;
            delete filtersData.minAge;
            delete filtersData.maxAge;
            delete filtersData.dateOfBirth;
        }

        if (selectedGender !== "") {
            filtersData.gender = selectedGender;
        } else {
            delete filtersData.gender;
        }

        if (selectedBloodGroup !== "") {
            filtersData.bloodGroup = selectedBloodGroup;
        } else {
            delete filtersData.bloodGroup;
        }

        if (selectedMaritalStatus !== "") {
            filtersData.maritalStatus = selectedMaritalStatus;
        } else {
            delete filtersData.maritalStatus;
        }

        if (pincode !== "") {
            filtersData.pincode = pincode;
        } else {
            delete filtersData.pincode;
        }

        if (city !== "") {
            filtersData.city = city;
        } else {
            delete filtersData.city;
        }

        if (state !== "") {
            filtersData.state = state;
        } else {
            delete filtersData.state;
        }

        if (selectedCountry !== "") {
            filtersData.country = selectedCountry;
        } else {
            delete filtersData.country;
        }

        if (phoneNumberDetails.number !== "") {
            filtersData.phoneNumberDetails = phoneNumberDetails
        } else {
            delete filtersData.phoneNumberDetails;
        }

        if (email !== "") {
            filtersData.email = email;
        } else {
            delete filtersData.email;
        }

        if (isUnmarriedMenSelected) {
            filtersData.unmarriedMen = true;
            filtersData.gender = "male";
            filtersData.maritalStatus = "Single";
        } else {
            delete filtersData.unmarriedMen;

            if (!selectedGender) {
                delete filtersData.gender;
            }

            if (!selectedMaritalStatus) {
                delete filtersData.maritalStatus;
            }
        }

        if (isUnmarriedWomenSelected) {
            filtersData.unmarriedWomen = true;
            filtersData.gender = "female";
            filtersData.maritalStatus = "Single";
        } else {
            delete filtersData.unmarriedWomen;

            if (!selectedGender) {
                delete filtersData.gender;
            }

            if (!selectedMaritalStatus) {
                delete filtersData.maritalStatus;
            }
        }

        if (isSasurVashinSelected) {
            filtersData.sasurVashin = true;
            filtersData.gender = "female";
        } else {
            delete filtersData.sasurVashin;

            if (!selectedGender) {
                delete filtersData.gender;
            }
        }

        if (isMaherVashinSelected) {
            filtersData.maherVashin = true;
            filtersData.gender = "female";
        } else {
            delete filtersData.maherVashin;

            if (!selectedGender) {
                delete filtersData.gender;
            }
        }

        if (isDirghayushiVyaktiSelected) {
            filtersData.dirghayushiVyakti = true;
        } else {
            delete filtersData.dirghayushiVyakti;
        }

        if (isShatayushiVyaktiSelected) {
            filtersData.shatayushiVyakti = true;
        } else {
            delete filtersData.shatayushiVyakti;
        }

        setSelectedFilters({...filtersData});
        setPreviousFilters({
            selectedFamilyTreeName, selectedDOBFilterByType, selectedMinAge, selectedMaxAge,
            selectedDateOfBirth, selectedMonth, selectedMonthYear, selectedYear,
            selectedGender, selectedBloodGroup, selectedMaritalStatus, pincode,
            city, state, selectedCountry, phoneNumberDetails,
            email, isUnmarriedMenSelected, isUnmarriedWomenSelected, isSasurVashinSelected,
            isMaherVashinSelected, isAdoptedSelected, isShatayushiVyaktiSelected, isDirghayushiVyaktiSelected
        });
        // setExistingFilters({...filtersData});
    }

    const getFilteredSearchResults = async (debouncedSearchQuery, selectedFilters) => {
        const filtersData = {
            searchQuery: Object.keys(selectedFilters).length === 0 ? debouncedSearchQuery : "",
            filters: selectedFilters
        }

        if (filtersData.searchQuery === "" && Object.keys(filtersData.filters).length === 0) {
            console.log("Everything Empty!");
            return;
        }

        const applyFiltersResponse = await getFilteredUsers(filtersData);
        console.log("Apply Filters Response: ", applyFiltersResponse);

        if (applyFiltersResponse.success) {
            setTimeout(() => {
                if (Object.keys(filtersData.filters).length > 0) {
                    console.log("Selected Filters are not empty!");
                    setSearchResults(applyFiltersResponse.data.results);
                    setSearchResultsCount(applyFiltersResponse.data.totalCount);
                    if (debouncedSearchQuery !== "") {
                        setFilteredSearchResults(
                            applyFiltersResponse.data.results
                                .filter((searchResult) =>
                                    searchResult.firstName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                    searchResult.middleName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                    (searchResult.firstName + " " + searchResult.middleName).toLowerCase().includes(debouncedSearchQuery.toLowerCase())
                                )
                                .slice(0, 100)
                        );
                    } else {
                        setFilteredSearchResults(applyFiltersResponse.data.results.slice(0, 100));
                    }
                    setCachedSearchResults({});
                } else {
                    console.log("Selected Filters are empty!");
                    setSearchResults(applyFiltersResponse.data.results);
                    setCachedSearchResults({
                        ...cachedSearchResults,
                        [debouncedSearchQuery]: {
                            totalCount: applyFiltersResponse.data.totalCount,
                            results: applyFiltersResponse.data.results.slice(0, 100)
                        }
                    });
                    setSearchResultsCount(applyFiltersResponse.data.totalCount);
                    setFilteredSearchResults(applyFiltersResponse.data.results.slice(0, 100));
                }
                console.log("Adding Results to Filtered Search Results!");
                setSearchResultsCount(applyFiltersResponse.data.totalCount);
                setIsSearchResultsLoading(false);
            }, 500);
        }
    }

    function formatBirthDate(dateString) {
        // Parse the ISO date string into a Date object
        const date = new Date(dateString);

        // Get the day of the month
        const day = date.getUTCDate();

        // Add the appropriate ordinal suffix to the day
        const dayWithSuffix = day + (day % 10 === 1 && day !== 11 ? 'st' :
            day % 10 === 2 && day !== 12 ? 'nd' :
                day % 10 === 3 && day !== 13 ? 'rd' : 'th');

        // Get the month as a short string
        const month = date.toLocaleString('en-US', {month: 'short'});

        // Get the year
        const year = date.getUTCFullYear();

        // Format the final output
        return `${dayWithSuffix} ${month}, ${year}`;
    }

    const handleClose = () => {
        setTimeout(() => {
            setSelectedProfileDetails(null);
        }, 200);
        setShowSelectedProfileDetailsModal(false);
    }

    const handleContact = () => {
        toast.info(t("CONSTANTS.COMING_SOON"));
    }

    useEffect(() => {
        const currentFilters = {
            selectedFamilyTreeName, selectedDOBFilterByType, selectedMinAge, selectedMaxAge,
            selectedDateOfBirth, selectedMonth, selectedMonthYear, selectedYear,
            selectedGender, selectedBloodGroup, selectedMaritalStatus, pincode,
            city, state, selectedCountry, phoneNumberDetails,
            email, isUnmarriedMenSelected, isUnmarriedWomenSelected, isSasurVashinSelected, isMaherVashinSelected,
            isAdoptedSelected, isShatayushiVyaktiSelected, isDirghayushiVyaktiSelected, showAllPeople
        };

        const hasChanged = !_.isEqual(previousFilters, currentFilters);

        if (_.isEqual(currentFilters, {
            selectedFamilyTreeName: "", selectedDOBFilterByType: "", selectedMinAge: "",
            selectedMaxAge: "", selectedDateOfBirth: "", selectedMonth: "",
            selectedMonthYear: "", selectedYear: "", selectedGender: "",
            selectedBloodGroup: "", selectedMaritalStatus: "", pincode: "",
            city: "", state: "", selectedCountry: "",
            phoneNumberDetails: {
                charCode: 'IN',
                dialingCode: '+91',
                countryName: 'India',
                number: ""
            },
            email: "",
            isUnmarriedMenSelected: false, isUnmarriedWomenSelected: false,
            isSasurVashinSelected: false, isMaherVashinSelected: false, isAdoptedSelected: false,
            isShatayushiVyaktiSelected: false, isDirghayushiVyaktiSelected: false, showAllPeople: true
        })) {
            setIsClearFiltersButtonDisabled(true);
            setIsApplyFiltersButtonDisabled(true);
        } else {
            setIsClearFiltersButtonDisabled(false);
            setIsApplyFiltersButtonDisabled(!hasChanged); // Enable only if filters have actually changed
        }

        if (isUpdatingFilters && !hasChanged) {
            setIsApplyFiltersButtonDisabled(false);
        }

        // setPreviousFilters(currentFilters); // Update previous filters after each render
    }, [
        selectedFamilyTreeName, selectedDOBFilterByType, selectedMinAge, selectedMaxAge,
        selectedDateOfBirth, selectedMonth, selectedMonthYear, selectedYear,
        selectedGender, selectedBloodGroup, selectedMaritalStatus, pincode,
        city, state, selectedCountry, phoneNumberDetails,
        email, isUnmarriedMenSelected, isUnmarriedWomenSelected, isSasurVashinSelected,
        isMaherVashinSelected, isAdoptedSelected, isShatayushiVyaktiSelected, isDirghayushiVyaktiSelected,
        showAllPeople, isUpdatingFilters
    ]);


    // ((Object.keys(selectedFilters).includes("familyTreeName") === false) && !(selectedFamilyTreeName === ""))
    // selectedFamilyTreeName === "" || selectedDOBFilterByType === "" || selectedMinAge === "" ||
    // selectedMaxAge === "" || selectedDateOfBirth === "" || selectedMonth === "" ||
    // selectedMonthYear === "" || selectedYear === "" || selectedGender === "" ||
    // selectedBloodGroup === "" || selectedMaritalStatus === "" || pincode === "" ||
    // city === "" && state === "" || selectedCountry === "" ||
    // phoneNumberDetails.number === "" || email === "" || !isUnmarriedMenSelected ||
    // !isUnmarriedWomenSelected || !isSasurVashinSelected || !isMaherVashinSelected ||
    // !isAdoptedSelected || !isShatayushiVyaktiSelected || !isDirghayushiVyaktiSelected

    // useEffect(() => {
    //     if (
    //         selectedFamilyTreeName === "" && selectedDOBFilterByType === "" && selectedMinAge === "" &&
    //         selectedMaxAge === "" && selectedDateOfBirth === "" && selectedMonth === "" &&
    //         selectedMonthYear === "" && selectedYear === "" && selectedGender === "" &&
    //         selectedBloodGroup === "" && selectedMaritalStatus === "" && pincode === "" &&
    //         city === "" && state === "" && selectedCountry === "" && phoneNumberDetails.number === "" && email === "" &&
    //         !isUnmarriedMenSelected && !isUnmarriedWomenSelected && !isSasurVashinSelected && !isMaherVashinSelected &&
    //         !isAdoptedSelected && !isShatayushiVyaktiSelected && !isDirghayushiVyaktiSelected
    //     ) {
    //         console.log("Inside All Empty Condition 1");
    //         console.log("Filtered Search Results: ", filteredSearchResults);
    //         setIsClearFiltersButtonDisabled(true);
    //
    //         if (isUpdatingFilters) {
    //             setIsApplyFiltersButtonDisabled(false);
    //         } else {
    //             setIsApplyFiltersButtonDisabled(true);
    //         }
    //     } else {
    //         console.log("Inside Some Empty Condition 2");
    //         setIsClearFiltersButtonDisabled(false);
    //         setIsApplyFiltersButtonDisabled(false);
    //     }
    // }, [
    //     selectedFamilyTreeName, selectedDOBFilterByType, selectedMinAge, selectedMaxAge,
    //     selectedDateOfBirth, selectedMonth, selectedMonthYear, selectedYear,
    //     selectedGender, selectedBloodGroup, selectedMaritalStatus, pincode,
    //     city, state, selectedCountry, phoneNumberDetails,
    //     email, isUnmarriedMenSelected, isUnmarriedWomenSelected, isSasurVashinSelected,
    //     isMaherVashinSelected, isAdoptedSelected, isShatayushiVyaktiSelected, isDirghayushiVyaktiSelected
    // ]);

    // Printing Cached Search Results
    useEffect(() => {
        // console.log("Updating Cached Search Results to ---> ", cachedSearchResults);
        localStorage.setItem("cachedSearchResults", LZString.compressToUTF16(JSON.stringify(cachedSearchResults)));
    }, [cachedSearchResults]);

    useEffect(() => {
        setIsSearchResultsLoading(true);
        console.log("Debounced Search Query ---> `" + debouncedSearchQuery + "`");

        if (debouncedSearchQuery === "" && Object.keys(selectedFilters).length === 0) {
            console.log("Condition 1");
            setSearchResultsCount(null);
            setSearchResults([]);
            setCachedSearchResults({});
            setFilteredSearchResults([]);
            setIsSearchResultsLoading(false);
        }

        if (debouncedSearchQuery === "" && Object.keys(selectedFilters).length > 0) {
            console.log("Condition 2");
            getFilteredSearchResults(debouncedSearchQuery, selectedFilters);
        }

        if (debouncedSearchQuery !== "" && Object.keys(selectedFilters).length === 0) {
            console.log("Condition 3");
            if (cachedSearchResults[debouncedSearchQuery]) {
                console.log("Condition 3.1");
                setTimeout(() => {
                    setSearchResultsCount(cachedSearchResults[debouncedSearchQuery].totalCount);
                    setFilteredSearchResults(cachedSearchResults[debouncedSearchQuery].results.slice(0, 100));
                    setIsSearchResultsLoading(false);
                }, 500);
            } else {
                console.log("Condition 3.2");
                console.log("Cached Results: ", cachedSearchResults);
                getFilteredSearchResults(debouncedSearchQuery, selectedFilters);
            }
        }

        if (debouncedSearchQuery !== "" && Object.keys(selectedFilters).length > 0) {
            console.log("Condition 4");
            setTimeout(() => {
                const tempFilteredResults = searchResults
                    .filter((searchResult) =>
                        searchResult.firstName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                        searchResult.middleName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                        (searchResult.firstName + " " + searchResult.middleName).toLowerCase().includes(debouncedSearchQuery.toLowerCase())
                    );

                setSearchResultsCount(tempFilteredResults.length);
                setFilteredSearchResults(tempFilteredResults.slice(0, 100));
                setIsSearchResultsLoading(false);
            }, 500);
        }
    }, [debouncedSearchQuery]);

    useEffect(() => {
        console.log("Selected Filters ---> ", selectedFilters);
        setIsSearchResultsLoading(true);

        if (Object.keys(selectedFilters).length === 0 && !isUpdatingFilters) {
            setIsClearFiltersButtonDisabled(true);
            if (debouncedSearchQuery === "") {
                setSearchResults([]);
                setFilteredSearchResults([]);
                setCachedSearchResults({});
                setSearchResultsCount(null);
                setIsSearchResultsLoading(false);
            } else {
                getFilteredSearchResults(debouncedSearchQuery, selectedFilters);
            }
        }

        if (Object.keys(selectedFilters).length > 0 && !isUpdatingFilters) {
            setIsClearFiltersButtonDisabled(false);
            getFilteredSearchResults(debouncedSearchQuery, selectedFilters);
        }
    }, [selectedFilters]);

    // Auto-Focus the Search Bar when Navigating to the Advanced Search Page
    useEffect(() => {
        console.log("AdvancedSearchPage Rendered!");
        const timeout = setTimeout(() => {
            console.log("Search Input Ref: ", searchInputRef);
            searchInputRef.current?.focus();
        }, 0);

        return () => {
            clearTimeout(timeout);
            setTimeout(() => {
                console.log("Clearing Search Query and Results!");
                setSearchQuery("");
                setSearchResults([]);
            }, 250);
        };
    }, []);

    // Export as Excel Functions
    const exportAsExcel = async () => {
        try {
            setIsExportAsExcelLoading(true);
            setIsExportAsExcelDisabled(true);

            let excelData = {};

            if (filteredSearchResults.length > 0) {
                excelData = {
                    worksheetName: "users",
                    headers: searchResultsExcelHeader,
                    data: searchResults.map((user) => ({
                        _id: user._id,
                        firstName: user.firstName ? user.firstName : '',
                        middleName: user.middleName ? user.middleName : '',
                        phoneNumber: user.phone && user.phone.number ? `${user.phone.dialingCode} ${user.phone.number}` : '',
                        emailId: user.email ? user.email : "",
                        familyTreeName: user.familyTreeName ? user.familyTreeName : '',
                    }))
                }
            }

            const response = await generateExcelFile(axiosPrivate, JSON.stringify(excelData));
            console.log("(AdvancedSearchPage.jsx) Generate Excel File Response: ", response);

            if (response.success) {
                const blob = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

                const url = window.URL.createObjectURL(blob);
                const downloadLink = document.createElement('a');
                downloadLink.href = url;
                // I want to file name to be in the format of "10th May 2025 - Event Name - Tickets List.xlsx"
                downloadLink.download = `${new Date().toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                }).replace(/^(\d{1,2})/, d => d + (["th", "st", "nd", "rd"][(d % 10 > 3 || (d % 100 >= 11 && d % 100 <= 13)) ? 0 : d % 10]))
                } - User Search Results List.xlsx`;

                downloadLink.click();
                downloadLink.remove();
                window.URL.revokeObjectURL(url);
            } else {
                toast.error("Something went wrong while exporting data!");
            }
        } catch (error) {
            console.error("(Events.jsx) Error exporting data: ", error);
            toast.error("Something went wrong while exporting data!");
        } finally {
            setIsExportAsExcelLoading(false);
            setIsExportAsExcelDisabled(false);
        }
    }

    useEffect(() => {
        if (filteredSearchResults.length > 0) {
            setIsExportAsExcelDisabled(false);
        } else {
            setIsExportAsExcelDisabled(true);
        }
    }, [filteredSearchResults]);

    return (
        <>
            <div className="w-[100dvw] h-[100dvh] bg-[#F7F7F7] flex items-center justify-center">
                <Header/>
                <div
                    className="min-h-full max-h-full min-w-full max-w-full
                        portrait:p-[0.75rem] portrait:gap-[0.75rem]
                        landscape:p-[0.5rem] landscape:gap-[0.5rem]

                        portrait:pt-[4rem] portrait:sm:pt-[5.5rem] portrait:jk:pt-[5rem]
                        landscape:pt-[3.5rem] landscape:jk:pt-[4.5rem]"
                >
                    <div
                        className="flex portrait:gap-[0.75rem] landscape:gap-[0.5rem] w-full
                            portrait:h-[calc(100dvh-4.75rem)] portrait:sm:h-[calc(100dvh-6.25rem)] portrait:jk:h-[calc(100dvh-5.75rem)]
                            landscape:h-[calc(100dvh-4rem)] landscape:jk:h-[calc(100dvh-5rem)]"
                    >
                        {/* Filters Section */}
                        {
                            isAuthenticated && (
                                <div
                                    className="hidden landscape:md:flex flex-col overflow-hidden
                                        landscape:md:w-[21rem] landscape:jk:w-[26rem] landscape:lg:w-[30rem]
                                        h-full rounded-xl bg-white shadow-xl "
                                >
                                    {/* Header */}
                                    <div
                                        className="pb-0 max-h-fit
                                            portrait:px-[1.25rem] portrait:pt-[1rem]
                                            portrait:jk:px-[1.75rem] portrait:jk:pt-[1.5rem]
                                            portrait:lg:p-[2.25rem] portrait:lg:pt-[2rem]

                                            landscape:px-[0.75rem] landscape:pt-[0.7rem]
                                            landscape:jk:px-[0.9rem] landscape:jk:pt-[0.85rem]
                                            landscape:lg:px-[1.15rem] landscape:lg:pt-[1rem]"
                                    >
                                        {/* Title */}
                                        <h2
                                            className="font-custom-bold text-[#3D3D3D]
                                                portrait:text-[1.25rem] portrait:md:text-[1.375rem]
                                                portrait:jk:text-[1.5rem] portrait:lg:text-[1.75rem]
                                                landscape:text-[0.85rem]
                                                landscape:jk:text-[1rem] landscape:lg:text-[1.225rem]"
                                        >
                                            {t("SEARCH_PAGE.FILTERS.TITLE")}
                                        </h2>

                                        {/* Divider */}
                                        <div
                                            className="h-[0.2vh] w-full bg-[#ECECEC] rounded-full
                                                portrait:mt-[0.5rem] portrait:jk:mt-[1.5rem] portrait:lg:mt-[2rem]
                                                landscape:mt-[0.2rem] landscape:jk:mt-[0.35rem] landscape:lg:mt-[0.5rem]"
                                        />
                                    </div>

                                    {/* Selected Filters | Filter Type Tab Bar */}
                                    <div
                                        className="h-fit flex flex-col gap-y-6
                                            overflow-y-scroll overflow-x-hidden

                                            portrait:px-[1.25rem] portrait:py-[1rem]
                                            portrait:jk:px-[1.75rem] portrait:jk:py-[1.5rem]
                                            portrait:lg:p-[2.25rem] portrait:lg:py-[2rem]

                                            landscape:px-[0.75rem] landscape:py-[0.7rem]
                                            landscape:jk:px-[0.9rem] landscape:jk:py-[0.85rem]
                                            landscape:lg:px-[1.15rem] landscape:lg:py-[1rem]"
                                    >
                                        {/* Selected Filters */}
                                        {
                                            (Object.keys(selectedFilters).length > 0 ||
                                                (Object.keys(selectedFilters).length === 1 && !Object.keys(selectedFilters).includes("showAllPeople"))) && (
                                                <div
                                                    className="flex-1 flex flex-col gap-2
                                                    overflow-y-scrollbar thin-scrollbar"
                                                >
                                                    <label className="text-[#292D32] text-[1vw] font-custom-semi-bold">
                                                        {t("SEARCH_PAGE.FILTERS.SELECTED_FILTERS")}
                                                    </label>

                                                    <div className={"flex-1 flex flex-wrap gap-[1.25vh]"}>
                                                        {/* Family Tree Name */}
                                                        {
                                                            selectedFilters.familyTreeName && (
                                                                <div
                                                                    className="flex items-center h-[5vh] w-fit
                                                                        bg-white p-[1vh] gap-[1.5vh]
                                                                        rounded-lg filters-box-shadow
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                >
                                                                    <div className={"flex items-center gap-[0.5vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[3vh] h-[3vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/people-icon-outlined.svg"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"Family Tree Name"}
                                                                            />
                                                                        </div>

                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                            {selectedFilters.familyTreeName}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setSelectedFamilyTreeName("");
                                                                            // setSelectedFilters(prevFilters => {
                                                                            //     const updatedFilters = { ...prevFilters }; // Create a new object
                                                                            //     delete updatedFilters.familyTreeName; // Modify the new object
                                                                            //     return updatedFilters; // Set state with new object
                                                                            // });
                                                                            delete selectedFilters.familyTreeName;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* D.O.B */}
                                                        {
                                                            selectedFilters.dobFilterByType && (
                                                                <div
                                                                    className="flex items-center h-[5vh] w-fit
                                                                        bg-white p-[1vh] gap-[1.5vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.5vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[2.5vh] h-[2.5vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/calendar-icon.svg"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"D.O.B"}
                                                                            />
                                                                        </div>

                                                                        <div className={"flex items-center gap-[0.75vh]"}>
                                                                            <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                                {t("SEARCH_PAGE.SELECTED_FILTERS.DOB")}
                                                                            </p>

                                                                            {
                                                                                selectedFilters.dobFilterByType === "age-range" && (
                                                                                    <div className={"flex items-center gap-[0.5vh]"}>
                                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                            {selectedFilters.minAge ? selectedFilters.minAge : ""}
                                                                                        </p>
                                                                                        <div
                                                                                            className="flex items-center justify-center
                                                                                                w-[2vh] h-[2vh] aspect-square"
                                                                                        >
                                                                                            <img
                                                                                                src={"/assets/age-range-icon-light.svg"}
                                                                                                className={"h-full aspect-square"}
                                                                                                alt={"Age Range"}
                                                                                            />
                                                                                        </div>
                                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                            {selectedFilters.maxAge ? selectedFilters.maxAge : ""}
                                                                                        </p>
                                                                                    </div>
                                                                                )
                                                                            }

                                                                            {
                                                                                selectedFilters.dobFilterByType === "date" && (
                                                                                    <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                        {selectedFilters.dateOfBirth}
                                                                                    </p>
                                                                                )
                                                                            }

                                                                            {
                                                                                selectedFilters.dobFilterByType === "month-year" && (
                                                                                    <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                        {selectedFilters.dateOfBirth}
                                                                                    </p>
                                                                                )
                                                                            }

                                                                            {
                                                                                selectedFilters.dobFilterByType === "month" && (
                                                                                    <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                        {monthLabel(selectedFilters.dateOfBirth)}
                                                                                    </p>
                                                                                )
                                                                            }

                                                                            {
                                                                                selectedFilters.dobFilterByType === "year" && (
                                                                                    <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                        {selectedFilters.dateOfBirth}
                                                                                    </p>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);

                                                                            setSelectedDOBFilterByType("");
                                                                            setSelectedMinAge("");
                                                                            setSelectedMaxAge("");
                                                                            setSelectedDateOfBirth("");
                                                                            setSelectedMonth("");
                                                                            setSelectedMonthYear("");
                                                                            setSelectedYear("");

                                                                            delete selectedFilters.dobFilterByType;
                                                                            delete selectedFilters.minAge;
                                                                            delete selectedFilters.maxAge;
                                                                            delete selectedFilters.dateOfBirth;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Gender */}
                                                        {
                                                            selectedFilters.gender && (
                                                                <div
                                                                    className="flex items-center gap-2 h-[5vh] w-fit
                                                                        bg-white p-[1vh] pl-[1.75vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <p className={"text-[#292D32] font-custom-semi-bold text-[1.75vh]"}>
                                                                        {genderLabel(selectedFilters.gender).label}
                                                                    </p>

                                                                    <div
                                                                        className="flex items-center justify-center
                                                                            w-[2.75vh] h-[2.75vh] aspect-square"
                                                                    >
                                                                        <img
                                                                            src={genderLabel(selectedFilters.gender).icon}
                                                                            className={"h-full aspect-square"}
                                                                            alt={"Gender"}
                                                                        />
                                                                    </div>

                                                                    <button
                                                                        disabled={selectedFilters.sasurVashin || selectedFilters.maherVashin || selectedFilters.unMarriedMen || selectedFilters.unMarriedWomen}
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setSelectedGender("");
                                                                            delete selectedFilters.gender;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Blood Group */}
                                                        {
                                                            selectedFilters.bloodGroup && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/blood-drop-icon.png"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"Blood Group"}
                                                                            />
                                                                        </div>

                                                                        <div className={"flex items-center gap-[0.75vh]"}>
                                                                            <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                                {t("SEARCH_PAGE.SELECTED_FILTERS.BLOOD_GROUP")}
                                                                            </p>
                                                                            <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                {selectedFilters.bloodGroup}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setSelectedBloodGroup("");
                                                                            delete selectedFilters.bloodGroup;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Marital Status */}
                                                        {
                                                            selectedFilters.maritalStatus && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/marriage-icon.png"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"Marriage"}
                                                                            />
                                                                        </div>

                                                                        <div className={"flex items-center gap-[0.75vh]"}>
                                                                            <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                                {t("SEARCH_PAGE.SELECTED_FILTERS.MARITAL_STATUS")}
                                                                            </p>
                                                                            <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                                {maritalStatusLabel(selectedFilters.maritalStatus)}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        disabled={selectedFilters.unMarriedMen || selectedFilters.unMarriedWomen}
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setSelectedMaritalStatus("");
                                                                            delete selectedFilters.maritalStatus;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Pincode */}
                                                        {
                                                            selectedFilters.pincode && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                    >
                                                                        <img
                                                                            src={"/assets/location-icon-outlined.svg"}
                                                                            className={"h-full aspect-square"}
                                                                            alt={"Pincode"}
                                                                        />
                                                                    </div>

                                                                    <div className={"flex items-center gap-[0.75vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.PINCODE")}
                                                                        </p>
                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                            {selectedFilters.pincode}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setPincode("");
                                                                            delete selectedFilters.pincode;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* City */}
                                                        {
                                                            selectedFilters.city && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.75vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.CITY")}
                                                                        </p>
                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                            {selectedFilters.city}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setCity("");
                                                                            delete selectedFilters.city;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* State */}
                                                        {
                                                            selectedFilters.state && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.75vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.STATE")}
                                                                        </p>
                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                            {selectedFilters.state}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setState("");
                                                                            delete selectedFilters.state;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Country */}
                                                        {
                                                            selectedFilters.country && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                    >
                                                                        <img
                                                                            src={"/assets/map-icon-outlined.svg"}
                                                                            className={"h-full aspect-square"}
                                                                            alt={"Country"}
                                                                        />
                                                                    </div>

                                                                    <div className={"flex items-center gap-[0.75vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-semi-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.COUNTRY")}
                                                                        </p>
                                                                        <p className={"text-[#292D32] font-custom-bold text-[1.75vh]"}>
                                                                            {selectedFilters.country}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setSelectedCountry("");
                                                                            delete selectedFilters.country;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Unmarried Men */}
                                                        {
                                                            selectedFilters.unMarriedMen && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/unmarried-icon.svg"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"Marriage"}
                                                                            />
                                                                        </div>

                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.UNMARRIED_MEN")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsUnmarriedMenSelected(false);
                                                                            delete selectedFilters.unMarriedMen;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Unmarried Women */}
                                                        {
                                                            selectedFilters.unMarriedWomen && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <div
                                                                            className="flex items-center justify-center
                                                                            w-[2.25vh] h-[2.25vh] aspect-square"
                                                                        >
                                                                            <img
                                                                                src={"/assets/unmarried-icon.svg"}
                                                                                className={"h-full aspect-square"}
                                                                                alt={"Marriage"}
                                                                            />
                                                                        </div>

                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.UNMARRIED_WOMEN")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsUnmarriedWomenSelected(false);
                                                                            delete selectedFilters.unMarriedWomen;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* SasurVashin */}
                                                        {
                                                            selectedFilters.sasurVashin && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        {/*<div*/}
                                                                        {/*    className="flex items-center justify-center*/}
                                                                        {/*    w-[2.25vh] h-[2.25vh] aspect-square"*/}
                                                                        {/*>*/}
                                                                        {/*    <img*/}
                                                                        {/*        src={"/assets/unmarried-icon.svg"}*/}
                                                                        {/*        className={"h-full aspect-square"}*/}
                                                                        {/*        alt={"Marriage"}*/}
                                                                        {/*    />*/}
                                                                        {/*</div>*/}

                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.SASURVASHIN")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsSasurVashinSelected(false);
                                                                            delete selectedFilters.sasurVashin;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* MaherVashin */}
                                                        {
                                                            selectedFilters.maherVashin && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        {/*<div*/}
                                                                        {/*    className="flex items-center justify-center*/}
                                                                        {/*    w-[2.25vh] h-[2.25vh] aspect-square"*/}
                                                                        {/*>*/}
                                                                        {/*    <img*/}
                                                                        {/*        src={"/assets/unmarried-icon.svg"}*/}
                                                                        {/*        className={"h-full aspect-square"}*/}
                                                                        {/*        alt={"Marriage"}*/}
                                                                        {/*    />*/}
                                                                        {/*</div>*/}

                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.MAHERVASHIN")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsMaherVashinSelected(false);
                                                                            delete selectedFilters.maherVashin;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Dirghayushi Vyakti */}
                                                        {
                                                            selectedFilters.dirghayushiVyakti && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.SHATAYUSHI_VYAKTI")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsDirghayushiVyaktiSelected(false);
                                                                            if (!isShatayushiVyaktiSelected) {
                                                                                setIsDOBFilterByTypeDisabled(false);
                                                                            }
                                                                            delete selectedFilters.dirghayushiVyakti;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }

                                                        {/* Shatayushi Vyakti */}
                                                        {
                                                            selectedFilters.shatayushiVyakti && (
                                                                <div
                                                                    className="flex items-center gap-[1.25vh] h-[5vh] w-fit
                                                                        bg-white p-[1vh]
                                                                        rounded-lg shadow-md  shadow-black/15
                                                                        border-2 border-[#EAEAEA]
                                                                        transition duration-300 ease-out"
                                                                    style={{
                                                                        boxShadow: "0px 4px 17px rgba(0, 0, 0, 0.1)"
                                                                    }}
                                                                >
                                                                    <div className={"flex items-center gap-[0.25vh]"}>
                                                                        <p className={"text-[#6D6D6D] font-custom-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.SELECTED_FILTERS.SHATAYUSHI_VYAKTI")}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => {
                                                                            setIsUpdatingFilters(true);
                                                                            setIsShatayushiVyaktiSelected(false);
                                                                            if (!isDirghayushiVyaktiSelected) {
                                                                                setIsDOBFilterByTypeDisabled(false);
                                                                            }
                                                                            delete selectedFilters.shatayushiVyakti;
                                                                        }}
                                                                        className={`h-full aspect-square rounded-md
                                                                            border-2 shadow-md shadow-black/10 
                                                                            bg-white border-[#CBCBCB]`}
                                                                    >
                                                                        <img
                                                                            src={"/assets/close-icon-light-grey.svg"}
                                                                            className={"w-full aspect-square"}
                                                                            alt={"Clear"}
                                                                            title={"Clear"}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        }

                                        {/* Basic Filters | Quick Access Tab Bar */}
                                        <div
                                            className="relative w-full flex-1 flex items-center
                                                landscape:md:min-h-[2.5rem] landscape:md:max-h-[2.5rem]
                                                landscape:jk:min-h-[2.75rem] landscape:jk:max-h-[2.75rem]
                                                landscape:lg:min-h-[3rem] landscape:lg:max-h-[3rem]
                                                rounded-xl bg-[#E5E5E5]"
                                        >
                                            <div
                                                className={`${isBasicFiltersTabSelected
                                                    ? 'landscape:left-[0.25rem]'
                                                    : 'landscape:left-[calc(50%+0.25rem)]'} 
                                                    landscape:md:h-[2rem] landscape:jk:h-[2.25rem] landscape:lg:h-[2.5rem]
                                                    w-[calc(50%-0.5rem)] flex-1 flex items-center justify-center
                                                    z-10 absolute bg-white rounded-lg border border-[#BEBEBE]`}
                                                style={{
                                                    transition: "all 150ms cubic-bezier(0.215, 0.61, 0.355, 1)",
                                                    boxShadow: "0px 3px 8px rgba(255, 102, 0, 0.12), 0px 3px 1px rgba(255, 102, 0, 0.04)"
                                                }}
                                            />
                                            <button
                                                className="flex-1 flex items-center justify-center
                                                    z-20 bg-transparent h-full"
                                                onClick={() => handleFilterTypeTabSelect("basic")}
                                            >
                                                <p
                                                    className="block font-custom-semi-bold leading-6 text-[#292D32]
                                                        portrait:text-[1rem] portrait:md:text-[0.8rem]
                                                        portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.65rem] landscape:md:text-[0.7rem]
                                                        landscape:jk:text-[0.75rem] landscape:lg:text-[0.85rem]"
                                                >
                                                    {t("SEARCH_PAGE.FILTERS.FILTER_TYPES.BASIC_FILTERS")}
                                                </p>
                                            </button>

                                            <button
                                                className="flex-1 flex items-center justify-center
                                                    z-20 bg-transparent h-full"
                                                onClick={() => handleFilterTypeTabSelect("quick-access")}
                                            >
                                                <p
                                                    className="block font-custom-semi-bold leading-6 text-[#292D32]
                                                        portrait:text-[1rem] portrait:md:text-[0.8rem]
                                                        portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.65rem] landscape:md:text-[0.7rem]
                                                        landscape:jk:text-[0.75rem] landscape:lg:text-[0.85rem]"
                                                >
                                                    {t("SEARCH_PAGE.FILTERS.FILTER_TYPES.QUICK_ACCESS")}
                                                </p>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Filters Container */}
                                    <div
                                        className="flex-1 flex w-[200%] overflow-x-hidden"
                                    >
                                        {/* Basic Filters */}
                                        <div
                                            className={`w-[50%] flex flex-col overflow-y-scroll thin-scrollbar
                                                portrait:gap-[0.85rem] portrait:md:gap-[1rem]
                                                portrait:jk:gap-[1.25rem] portrait:lg:gap-[1.75rem]
                
                                                landscape:gap-[0.6rem] landscape:md:gap-[0.75rem]
                                                landscape:jk:gap-[1rem] landscape:lg:gap-[1.25rem]
                                            ${isBasicFiltersTabSelected ? 'ml-[0%]' : '-ml-[50%]'}`}
                                            style={{transition: "all 150ms cubic-bezier(0.215, 0.61, 0.355, 1)"}}
                                        >
                                            {/* D.O.B | Gender */}
                                            <div className="custom-input-multiple-fields-container px-[1.25vw]">
                                                {/* Date of Birth */}
                                                <div className="custom-input-container">
                                                    <label
                                                        aria-disabled={isDOBFilterByTypeDisabled}
                                                        // className="text-[#292D32] text-[1vw] font-custom-semi-bold"
                                                    >
                                                        {t("SEARCH_PAGE.FILTERS.DOB.LABEL")}
                                                    </label>

                                                    {/**/}
                                                    <div className={"custom-input-multiple-fields-container"}>
                                                        {/* D.O.B Filter By Type */}
                                                        <div
                                                            className="min-w-fit w-full flex-1 flex flex-col
                                                                rounded-md shadow-lg shadow-black/5"
                                                        >
                                                            <button
                                                                disabled={isDOBFilterByTypeDisabled}
                                                                onBlur={() => {
                                                                    if (!isSelectingDOBFilterByType) {
                                                                        setIsDOBFilterByTypeDropdownOpen(false);
                                                                    }
                                                                }}
                                                                className={`custom-input-dropdown-button
                                                                ${isDOBFilterByTypeDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}
                                                                transition-all duration-150 ease-in-out`}
                                                                onClick={() => setIsDOBFilterByTypeDropdownOpen(!isDOBFilterByTypeDropdownOpen)}
                                                            >
                                                                <div className={""}>
                                                                    <p className={"!font-custom-semi-bold"}>
                                                                        {dobFilterByTypeLabel(selectedDOBFilterByType)}
                                                                    </p>
                                                                </div>
                                                                <ChevronDownIcon
                                                                    className={`${isDOBFilterByTypeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                    aria-hidden="true"
                                                                    stroke={"#292D32"}
                                                                    strokeWidth={"0.1vh"}
                                                                />
                                                            </button>

                                                            {/* D.O.B Filter By Type Dropdown */}
                                                            <div className={"relative"}>
                                                                <Transition show={isDOBFilterByTypeDropdownOpen}>
                                                                    <div
                                                                        className={`bg-white absolute top-1 left-0 right-0 z-[100] max-h-[15vh]
                                                                        origin-top-right rounded-md shadow-2xl 
                                                                        border border-gray-700 overflow-y-scroll
                                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                    >
                                                                        {
                                                                            dobFilterByTypeOptions.map((dobFilterByType, index) => (
                                                                                <button
                                                                                    key={index}
                                                                                    className="custom-input-dropdown-list-button"
                                                                                    onMouseDown={() => setIsSelectingDOBFilterByType(true)}
                                                                                    onClick={() => handleDOBFilterByTypeSelect(dobFilterByType.value)}
                                                                                >
                                                                                    <p className={""}>
                                                                                        {dobFilterByType.label}
                                                                                    </p>
                                                                                </button>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </Transition>
                                                            </div>
                                                        </div>

                                                        {
                                                            selectedDOBFilterByType === "age-range" && (
                                                                <div
                                                                    className="h-full flex items-center justify-between
                                                                        landscape:md:w-[40%] landscape:jk:w-[35%]"
                                                                >
                                                                    {/* Min-Age */}
                                                                    <input
                                                                        type={"tel"}
                                                                        className={`h-full w-1/3 bg-transparent rounded-md
                                                                            portrait:text-[1.25rem] portrait:md:text-[1rem]
                                                                            portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                                            landscape:text-[0.8rem] landscape:jk:text-[0.9rem]
                                                                            landscape:lg:text-[1rem]
        
                                                                            leading-tight text-gray-900 font-custom-semi-bold text-center
                                                                            border border-gray-300 outline-none shadow-lg shadow-black/5
                                                                            focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]
                                                                            transition-all duration-150 ease-in-out`}
                                                                        maxLength={2}
                                                                        value={selectedMinAge}
                                                                        onChange={(e) => {
                                                                            setSelectedMinAge(e.target.value);
                                                                            // setExistingFilters({
                                                                            //     ...existingFilters,
                                                                            //     minAge: e.target.value
                                                                            // })
                                                                        }}
                                                                    />

                                                                    {/* Age Range Icon */}
                                                                    <img
                                                                        className={"h-[55%] w-1/3 aspect-square"}
                                                                        src={"/assets/age-range-icon-light.svg"}
                                                                        alt={"Age Range"}
                                                                    />

                                                                    {/* Max-Age */}
                                                                    <input
                                                                        type={"number"}
                                                                        className={`h-full w-1/3 bg-transparent rounded-md
                                                                            portrait:text-[1.25rem] portrait:md:text-[1rem]
                                                                            portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                                            landscape:text-[0.8rem] landscape:jk:text-[0.9rem]
                                                                            landscape:lg:text-[1rem]
        
                                                                            leading-tight text-gray-900 font-custom-semi-bold text-center
                                                                            border border-gray-300 outline-none shadow-lg shadow-black/5
                                                                            focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]
                                                                            transition-all duration-150 ease-in-out`}
                                                                        maxLength={2}
                                                                        value={selectedMaxAge}
                                                                        onChange={(e) => {
                                                                            setSelectedMaxAge(e.target.value);
                                                                            // setExistingFilters({
                                                                            //     ...existingFilters,
                                                                            //     maxAge: e.target.value
                                                                            // });
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }

                                                        {
                                                            selectedDOBFilterByType === "date" && (
                                                                <input
                                                                    type="date"
                                                                    min={"1900-01-01"}
                                                                    required
                                                                    value={selectedDateOfBirth}
                                                                    onChange={(e) => {
                                                                        setSelectedDateOfBirth(e.target.value);
                                                                        // setExistingFilters({
                                                                        //     ...existingFilters,
                                                                        //     dateOfBirth: e.target.value
                                                                        // });
                                                                    }}
                                                                    className={`custom-input !font-custom-semi-bold
                                                                        !shadow-lg !shadow-black/5 !bg-white`}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            selectedDOBFilterByType === "month-year" && (
                                                                <input
                                                                    type="month"
                                                                    max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                                                                    required
                                                                    value={selectedDateOfBirth}
                                                                    onChange={(e) => {
                                                                        setSelectedDateOfBirth(e.target.value);
                                                                        // setExistingFilters({
                                                                        //     ...existingFilters,
                                                                        //     dateOfBirth: e.target.value
                                                                        // });
                                                                    }}
                                                                    className={`custom-input !font-custom-semi-bold
                                                                        !shadow-lg !shadow-black/5 !bg-white`}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            selectedDOBFilterByType === "month" && (
                                                                // Month Dropdown
                                                                <div
                                                                    className="relative h-full w-full flex-1 flex flex-col
                                                                    rounded-md shadow-lg shadow-black/5"
                                                                >
                                                                    <button
                                                                        onBlur={() => {
                                                                            if (!isSelectingMonth) {
                                                                                setIsMonthDropdownOpen(false);
                                                                            }
                                                                        }}
                                                                        className={`custom-input-dropdown-button
                                                                            ${isMonthDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
                                                                        onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                                                                    >
                                                                        <div
                                                                            className={""}>
                                                                            <p className={"!font-custom-semi-bold"}>
                                                                                {monthLabel(selectedMonth)}
                                                                            </p>
                                                                        </div>
                                                                        <ChevronDownIcon
                                                                            className={`${isMonthDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                            aria-hidden="true"
                                                                            stroke="#292D32"
                                                                            strokeWidth={"0.1vh"}
                                                                        />
                                                                    </button>

                                                                    {/* Month Dropdown */}
                                                                    <div className={"relative"}>
                                                                        <Transition show={isMonthDropdownOpen}>
                                                                            <div
                                                                                className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                                    origin-top-right rounded-md shadow-2xl 
                                                                                    border border-gray-700 overflow-y-scroll
                                                                                    data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                                    data-[enter]:duration-100 data-[enter]:ease-out 
                                                                                    data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                            >
                                                                                {
                                                                                    monthsList.map((month, index) => (
                                                                                        <button
                                                                                            key={index}
                                                                                            className="custom-input-dropdown-list-button"
                                                                                            onMouseDown={() => setIsSelectingMonth(true)}
                                                                                            onClick={() => handleMonthSelect(month.value)}
                                                                                        >
                                                                                            <p className={"!font-custom-semi-bold"}>
                                                                                                {month.label}
                                                                                            </p>
                                                                                        </button>
                                                                                    ))
                                                                                }
                                                                            </div>
                                                                        </Transition>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }

                                                        {
                                                            selectedDOBFilterByType === "year" && (
                                                                <input
                                                                    type={"tel"}
                                                                    className={`custom-input !font-custom-semi-bold
                                                                        !shadow-lg !shadow-black/5 !bg-white`}
                                                                    maxLength={4}
                                                                    value={selectedYear}
                                                                    onChange={(e) => {
                                                                        setSelectedYear(e.target.value);
                                                                        // setExistingFilters({
                                                                        //     ...existingFilters,
                                                                        //     dateOfBirth: e.target.value
                                                                        // });
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </div>
                                                </div>

                                                {/* Gender */}
                                                <div className="custom-input-container landscape:md:!w-[40%]">
                                                    <label
                                                        aria-disabled={isGenderDisabled}
                                                        // className="text-[#292D32] text-[1vw] font-custom-semi-bold"
                                                        htmlFor="gender"
                                                    >
                                                        {t("SEARCH_PAGE.FILTERS.GENDER.LABEL")}
                                                    </label>

                                                    {/* Gender Dropdown */}
                                                    <div
                                                        className="relative h-full w-full flex flex-col
                                                            rounded-md shadow-lg shadow-black/5"
                                                    >
                                                        <button
                                                            disabled={isGenderDisabled}
                                                            onBlur={() => {
                                                                if (!isSelectingGender) {
                                                                    setIsGenderDropdownOpen(false);
                                                                }
                                                            }}
                                                            className={`custom-input-dropdown-button
                                                                ${isGenderDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}
                                                                ${isGenderDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}
                                                            transition-all duration-150 ease-in-out`}
                                                            onClick={() => setIsGenderDropdownOpen(!isGenderDropdownOpen)}
                                                        >
                                                            <div className={""}>
                                                                <p className={"!font-custom-semi-bold"}>
                                                                    {genderLabel(selectedGender).label}
                                                                </p>

                                                                {
                                                                    selectedGender !== "" && (
                                                                        <img
                                                                            className={""}
                                                                            src={genderLabel(selectedGender).icon}
                                                                            alt={genderLabel(selectedGender).label}
                                                                        />
                                                                    )
                                                                }
                                                            </div>
                                                            <ChevronDownIcon
                                                                className={`${isGenderDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                aria-hidden="true"
                                                                stroke={"#292D32"}
                                                                strokeWidth={"0.1vh"}
                                                            />
                                                        </button>

                                                        {/* Gender Dropdown */}
                                                        <div className={"relative"}>
                                                            <Transition show={isGenderDropdownOpen}>
                                                                <div
                                                                    className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                        origin-top-right rounded-md shadow-2xl 
                                                                        border border-gray-700 overflow-y-scroll
                                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                >
                                                                    <button
                                                                        className="custom-input-dropdown-list-button"
                                                                        onMouseDown={() => setIsSelectingGender(true)}
                                                                        onClick={() => handleGenderSelect("")}
                                                                    >
                                                                        <p className={""}>
                                                                            {t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.DEFAULT")}
                                                                        </p>
                                                                    </button>
                                                                    <button
                                                                        className="custom-input-dropdown-list-button"
                                                                        onMouseDown={() => setIsSelectingGender(true)}
                                                                        onClick={() => handleGenderSelect("male")}
                                                                    >
                                                                        <p className={"font-custom-semi-bold text-[1.75vh]"}>
                                                                            {t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.MALE")}
                                                                        </p>

                                                                        <img
                                                                            className={""}
                                                                            src={"/assets/male-gender-icon-bold.png"}
                                                                            alt={t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.MALE")}
                                                                        />
                                                                    </button>
                                                                    <button
                                                                        className="custom-input-dropdown-list-button"
                                                                        onMouseDown={() => setIsSelectingGender(true)}
                                                                        onClick={() => handleGenderSelect("female")}
                                                                    >
                                                                        <p className={""}>
                                                                            {t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.FEMALE")}
                                                                        </p>

                                                                        <img
                                                                            className={""}
                                                                            src={"/assets/female-gender-icon-bold.png"}
                                                                            alt={t("SEARCH_PAGE.FILTERS.GENDER.OPTIONS.FEMALE")}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </Transition>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Blood Group || Marital Status || PinCode */}
                                            <div className={`custom-input-multiple-fields-container px-[1.25vw]`}>
                                                {/* Blood Group */}
                                                <div className={`custom-input-container`}>
                                                    {/* Label */}
                                                    <label htmlFor="bloodGroup">
                                                        <img
                                                            src={"/assets/blood-drop-icon.png"}
                                                            alt={"Blood Group"}
                                                            title={"Blood Group"}
                                                        />
                                                        {t("SEARCH_PAGE.FILTERS.BLOOD_GROUP.LABEL")}
                                                    </label>

                                                    {/* Dropdown */}
                                                    <div
                                                        className="relative h-full w-full flex flex-col
                                                            rounded-md shadow-lg shadow-black/5"
                                                    >
                                                        <button
                                                            onBlur={() => {
                                                                if (!isSelectingBloodGroup) {
                                                                    setIsBloodGroupDropdownOpen(false);
                                                                }
                                                            }}
                                                            className={`custom-input-dropdown-button
                                                                ${isBloodGroupDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
                                                            onClick={() => setIsBloodGroupDropdownOpen(!isBloodGroupDropdownOpen)}
                                                        >
                                                            <p className={"!font-custom-semi-bold"}>
                                                                {
                                                                    selectedBloodGroup === ""
                                                                        ? t("SEARCH_PAGE.FILTERS.BLOOD_GROUP.DEFAULT")
                                                                        : selectedBloodGroup
                                                                }
                                                            </p>
                                                            <ChevronDownIcon
                                                                className={`${isBloodGroupDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                aria-hidden="true"
                                                                stroke="#292D32"
                                                                strokeWidth={"0.1vh"}
                                                            />
                                                        </button>

                                                        {/* Blood Group Dropdown */}
                                                        <div className={"relative"}>
                                                            <Transition show={isBloodGroupDropdownOpen}>
                                                                <div
                                                                    className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                        origin-top-right rounded-md shadow-2xl 
                                                                        border border-gray-700 overflow-y-scroll
                                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                >
                                                                    {
                                                                        bloodGroups.map((bloodGroup, index) => (
                                                                            <button
                                                                                key={index}
                                                                                className="custom-input-dropdown-list-button"
                                                                                onMouseDown={() => setIsSelectingBloodGroup(true)}
                                                                                onClick={() => handleBloodGroupSelect(bloodGroup)}
                                                                            >
                                                                                <p className={""}>
                                                                                    {bloodGroup}
                                                                                </p>
                                                                            </button>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </Transition>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Marital Status */}
                                                <div className={`custom-input-container`}>
                                                    {/* Label */}
                                                    <label
                                                        aria-disabled={isMaritalStatusDisabled}
                                                        htmlFor="maritalStatus"
                                                    >
                                                        <img
                                                            src={"/assets/marriage-icon.png"}
                                                            alt={"Marriage"}
                                                            title={"Marriage"}
                                                        />
                                                        {t("SEARCH_PAGE.FILTERS.MARITAL_STATUS.LABEL")}
                                                    </label>

                                                    {/* Dropdown */}
                                                    <div
                                                        className="relative h-full w-full flex flex-col
                                                            rounded-md shadow-lg shadow-black/5"
                                                    >
                                                        <button
                                                            disabled={isMaritalStatusDisabled}
                                                            onBlur={() => {
                                                                if (!isSelectingMaritalStatus) {
                                                                    setIsMaritalStatusDropdownOpen(false);
                                                                }
                                                            }}
                                                            className={`custom-input-dropdown-button
                                                                ${isMaritalStatusDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}
                                                                ${isMaritalStatusDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}`}
                                                            onClick={() => setIsMaritalStatusDropdownOpen(!isMaritalStatusDropdownOpen)}
                                                        >
                                                            <p className={"!font-custom-semi-bold"}>
                                                                {maritalStatusLabel(selectedMaritalStatus)}
                                                            </p>
                                                            <ChevronDownIcon
                                                                className={`${isMaritalStatusDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                aria-hidden="true"
                                                                stroke="#292D32"
                                                                strokeWidth={"0.1vh"}
                                                            />
                                                        </button>

                                                        {/* Marital Status Dropdown */}
                                                        <div className={"relative"}>
                                                            <Transition show={isMaritalStatusDropdownOpen}>
                                                                <div
                                                                    className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                    origin-top-right rounded-md shadow-2xl 
                                                                    border border-gray-700 overflow-y-scroll
                                                                    data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                    data-[enter]:duration-100 data-[enter]:ease-out 
                                                                    data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                >
                                                                    {
                                                                        maritalStatusOptions.map((maritalStatus, index) => (
                                                                            <button
                                                                                key={index}
                                                                                className="custom-input-dropdown-list-button"
                                                                                onMouseDown={() => setIsSelectingMaritalStatus(true)}
                                                                                onClick={() => handleMaritalStatusSelect(maritalStatus.value)}
                                                                            >
                                                                                <p className={"!font-custom-semi-bold"}>
                                                                                    {maritalStatus.label}
                                                                                </p>
                                                                            </button>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </Transition>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* PinCode */}
                                                <div className={`custom-input-container`}>
                                                    <label htmlFor="pincode">
                                                        {t("SEARCH_PAGE.FILTERS.PINCODE")}
                                                    </label>
                                                    <div
                                                        className={`custom-input-w-trailing-icon
                                                            ${isPincodeInputFocused && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
                                                    >
                                                        <img
                                                            src={"/assets/search-icon-light.svg"}
                                                            alt="Search Icon"
                                                        />

                                                        <input
                                                            name={"pincode"}
                                                            type={"tel"}
                                                            maxLength={6}
                                                            value={pincode}
                                                            onBlur={() => setIsPincodeInputFocused(false)}
                                                            onFocus={() => setIsPincodeInputFocused(true)}
                                                            onChange={handlePincodeChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* City, State, Country */}
                                            <div className={`custom-input-multiple-fields-container px-[1.25vw]`}>
                                                {/* City */}
                                                <div className={`custom-input-container`}>
                                                    <label htmlFor={`city`}>
                                                        {t("SEARCH_PAGE.FILTERS.CITY")}
                                                    </label>
                                                    <div
                                                        className={`custom-input-w-trailing-icon
                                                            ${isCityInputFocused && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
                                                    >
                                                        <img
                                                            src={"/assets/search-icon-light.svg"}
                                                            alt="Search Icon"
                                                        />

                                                        <input
                                                            name={"city"}
                                                            type={"text"}
                                                            value={city}
                                                            onBlur={() => setIsCityInputFocused(false)}
                                                            onFocus={() => setIsCityInputFocused(true)}
                                                            onChange={handleCityChange}
                                                        />
                                                    </div>
                                                </div>

                                                {/* State */}
                                                <div className={`custom-input-container`}>
                                                    <label htmlFor={"state"}>
                                                        {t("SEARCH_PAGE.FILTERS.STATE")}
                                                    </label>
                                                    <div
                                                        className={`custom-input-w-trailing-icon
                                                            ${isStateInputFocused && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
                                                    >
                                                        <img
                                                            src={"/assets/search-icon-light.svg"}
                                                            alt="Search Icon"
                                                        />

                                                        <input
                                                            name={"state"}
                                                            type={"text"}
                                                            value={state}
                                                            onBlur={() => setIsStateInputFocused(false)}
                                                            onFocus={() => setIsStateInputFocused(true)}
                                                            onChange={handleStateChange}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Country */}
                                                <div className={`custom-input-container`}>
                                                    <label htmlFor="country">
                                                        {t("SEARCH_PAGE.FILTERS.COUNTRY.LABEL")}
                                                    </label>
                                                    <div
                                                        className="relative h-full w-full flex flex-col
                                                            shadow-lg shadow-black/5"
                                                    >
                                                        {/* Country Value */}
                                                        <div
                                                            onBlur={() => {
                                                                if (!isSelectingCountry) {
                                                                    setIsCountryDropdownOpen(false);
                                                                }
                                                            }}
                                                            className={`custom-input-dropdown-button-w-trailing-icon`}
                                                            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                                                        >
                                                            {/* Search Icon */}
                                                            <img
                                                                src={"/assets/search-icon-light.svg"}
                                                                alt="Search Icon"
                                                            />

                                                            <input
                                                                id="country"
                                                                name="country"
                                                                className={"!font-custom-semi-bold"}
                                                                value={selectedCountry}
                                                                onChange={(e) => {
                                                                    if (e.target.value === "") {
                                                                        setFilteredCountryDetails(countryCodes);
                                                                    } else {
                                                                        console.log("Country Input Value: ", e.target.value);
                                                                        setFilteredCountryDetails(
                                                                            countryCodes
                                                                                .filter((country) => {
                                                                                    return country.countryName.toLowerCase().includes(e.target.value.toLowerCase());
                                                                                })
                                                                        );
                                                                    }
                                                                    setSelectedCountry(e.target.value);
                                                                }}
                                                            />

                                                            <ChevronDownIcon
                                                                className={`${isCountryDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                                                                aria-hidden="true"
                                                                stroke="#292D32"
                                                                strokeWidth={"0.1vh"}
                                                            />
                                                        </div>

                                                        {/* Country Dropdown */}
                                                        <div className={"relative"}>
                                                            <Transition show={isCountryDropdownOpen}>
                                                                <div
                                                                    className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                        origin-top-right rounded-md shadow-2xl 
                                                                        border border-gray-700 overflow-y-scroll
                                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                                >
                                                                    {
                                                                        filteredCountryDetails.length > 0
                                                                            ? filteredCountryDetails.map((countryDetails, index) => (
                                                                                <button
                                                                                    key={index}
                                                                                    className={`custom-input-dropdown-list-button text-wrap flex-wrap`}
                                                                                    onMouseDown={() => setIsSelectingCountry(true)}
                                                                                    onClick={() => handleCountrySelect(countryDetails.countryName)}
                                                                                >
                                                                                    <p
                                                                                        className="text-left text-nowrap !font-custom-semi-bold
                                                                                            overflow-x-scroll no-scrollbar "
                                                                                    >
                                                                                        {countryDetails.countryName}
                                                                                    </p>
                                                                                </button>
                                                                            )) : (
                                                                                <button
                                                                                    className={`custom-input-dropdown-list-button text-wrap flex-wrap`}
                                                                                    onClick={() => setIsCountryDropdownOpen(false)}
                                                                                >
                                                                                    <p
                                                                                        className="text-left text-nowrap !font-custom-semi-bold
                                                                                            overflow-x-scroll no-scrollbar "
                                                                                    >
                                                                                        {selectedCountry}
                                                                                    </p>
                                                                                </button>
                                                                            )
                                                                    }
                                                                </div>
                                                            </Transition>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Mobile Number */}
                                            <div className={"custom-phone-number-input px-[1.25vw]"}>
                                                <label htmlFor="phone">
                                                    {t("SEARCH_PAGE.FILTERS.PHONE_NUMBER")}
                                                </label>
                                                <div
                                                    className="h-full w-full flex flex-col
                                                    rounded-md  shadow-lg shadow-black/5"
                                                >
                                                    {/* Country Code | Phone Number */}
                                                    <div className="custom-phone-number-input-container">
                                                        {/* Country Code */}
                                                        <button
                                                            onBlur={() => {
                                                                if (!isSelectingCountryCode) {
                                                                    console.log("BLUR CLICKED!");
                                                                    setIsCountryCodeDropdownOpen(false);
                                                                }
                                                            }}
                                                            onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                                        >
                                                            <img
                                                                src={`/assets/country_flags/${phoneNumberDetails.charCode}.png`}
                                                                alt={phoneNumberDetails.countryName}
                                                            />
                                                            <p aria-selected={"false"}>
                                                                {phoneNumberDetails.dialingCode}
                                                            </p>
                                                            <ChevronDownIcon
                                                                // onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                                                className={`${isCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                                aria-hidden="true"
                                                                stroke="#292D32"
                                                                strokeWidth={"0.1vh"}
                                                            />
                                                        </button>

                                                        {/* Phone Number */}
                                                        <input
                                                            onBlur={() => {
                                                                if (!isSelectingCountryCode) {
                                                                    setIsCountryCodeDropdownOpen(false);
                                                                }
                                                            }}
                                                            className={"!font-custom-semi-bold"}
                                                            type="tel"
                                                            name="phone"
                                                            placeholder="7734567890"
                                                            value={phoneNumberDetails.number || ""}
                                                            onChange={handlePhoneNumberChange}
                                                            maxLength={11}
                                                        />
                                                    </div>

                                                    {/* Country Code Dropdown */}
                                                    <div className={"relative"}>
                                                        <Transition show={isCountryCodeDropdownOpen}>
                                                            <div
                                                                className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                    origin-top-right rounded-md shadow-2xl 
                                                                    border border-gray-700 overflow-y-scroll
                                                                    data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                    data-[enter]:duration-100 data-[enter]:ease-out 
                                                                    data-[leave]:duration-75 data-[leave]:ease-in`}
                                                            >
                                                                {
                                                                    countryCodes.map((country, index) => (
                                                                        <button
                                                                            key={index}
                                                                            className="custom-phone-number-input-dropdown-button hover:bg-gray-300"
                                                                            onMouseDown={() => setIsSelectingCountryCode(true)}
                                                                            onClick={() => handleCountryCodeSelect(country)}
                                                                        >
                                                                            <img
                                                                                src={country.countryFlagSrc}
                                                                                alt={country.countryName}
                                                                            />
                                                                            <p
                                                                                className={"!font-custom-semi-bold"}
                                                                                aria-selected={"false"}
                                                                            >
                                                                                {country.countryName}
                                                                            </p>
                                                                            <p
                                                                                className={"!font-custom-semi-bold"}
                                                                                aria-selected={"false"}
                                                                            >
                                                                                {'(' + country.countryDialingCode + ')'}
                                                                            </p>
                                                                        </button>
                                                                    ))
                                                                }
                                                            </div>
                                                        </Transition>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Email */}
                                            <div className={"custom-input-container px-[1.25vw]"}>
                                                <label htmlFor={"email"}>
                                                    {t("SEARCH_PAGE.FILTERS.EMAIL.LABEL")}
                                                </label>
                                                <div className={`custom-input-w-trailing-icon`}>
                                                    <img
                                                        src={"/assets/search-icon-light.svg"}
                                                        alt="Search Icon"
                                                    />

                                                    <input
                                                        type={"text"}
                                                        value={email}
                                                        placeholder={t("SEARCH_PAGE.FILTERS.EMAIL.PLACEHOLDER")}
                                                        onBlur={() => setIsEmailInputFocused(false)}
                                                        onFocus={() => setIsEmailInputFocused(true)}
                                                        onChange={handleEmailChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quick Access */}
                                        <div
                                            className={`w-[50%] flex flex-col gap-y-6 overflow-y-scroll thin-scrollbar`}
                                            style={{transition: "all 150ms cubic-bezier(0.215, 0.61, 0.355, 1)"}}
                                        >
                                            {/* Family Tree Name */}
                                            <div className={"custom-input-container px-[1.25vw]"}>
                                                <label htmlFor={"familyTreeName"}>
                                                    {t("SEARCH_PAGE.FILTERS.FAMILY_TREE_NAME.LABEL")}
                                                </label>

                                                <div
                                                    className="relative h-full w-full flex flex-col
                                                        shadow-lg shadow-black/5 rounded-md"
                                                >
                                                    {/* Family Tree Name Search Input */}
                                                    <div className={`custom-input-dropdown-button-w-trailing-icon`}>
                                                        <img
                                                            src={"/assets/search-icon-light.svg"}
                                                            alt="Search Icon"
                                                        />

                                                        <input
                                                            ref={familyTreeNameSearchInputRef}
                                                            id="familyTreeName"
                                                            className={"!font-custom-semi-bold"}
                                                            name="familyTreeName"
                                                            value={selectedFamilyTreeName}
                                                            onBlur={(e) => {
                                                                if (!isSelectingFamilyTreeName) {
                                                                    setIsFamilyTreeNameSearchInputBlurred(true);
                                                                    setIsFamilyTreeNameDropdownOpen(false);
                                                                }
                                                            }}
                                                            onClick={() => setIsFamilyTreeNameDropdownOpen(true)}
                                                            onKeyUp={(e) => {
                                                                if (e.key === "Escape") {
                                                                    setIsFamilyTreeNameDropdownOpen(false);
                                                                }
                                                            }}
                                                            onFocus={(e) => {
                                                                setIsFamilyTreeNameSearchInputBlurred(false);
                                                                if (e.target.value === "") {
                                                                    setFilteredFamilyTreeNames(familyTreeNames);
                                                                } else {
                                                                    console.log("Family Tree Name Input Value: ", e.target.value);
                                                                    setFilteredFamilyTreeNames(
                                                                        familyTreeNames
                                                                            .filter((familyTreeName) => {
                                                                                return familyTreeName.toLowerCase().includes(e.target.value.toLowerCase());
                                                                            })
                                                                    );
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                if (e.target.value === "") {
                                                                    setFilteredFamilyTreeNames(familyTreeNames);
                                                                } else {
                                                                    console.log("Family Tree Name Input Value: ", e.target.value);
                                                                    setFilteredFamilyTreeNames(
                                                                        familyTreeNames
                                                                            .filter((familyTreeName) => {
                                                                                return familyTreeName.toLowerCase().includes(e.target.value.toLowerCase());
                                                                            })
                                                                    );
                                                                }

                                                                setSelectedFamilyTreeName(e.target.value);
                                                            }}
                                                        />

                                                        <ChevronDownIcon
                                                            className={`${isFamilyTreeNameDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                                                            aria-hidden="true"
                                                            stroke={"#292D32"}
                                                            strokeWidth={"0.1vh"}
                                                            onClick={() => {
                                                                if (isFamilyTreeNameSearchInputBlurred && !isFamilyTreeNameDropdownOpen) {
                                                                    setIsFamilyTreeNameDropdownOpen(false);
                                                                } else {
                                                                    setIsFamilyTreeNameDropdownOpen(true);
                                                                }
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Family Tree Name Dropdown */}
                                                    <div className={"relative"}>
                                                        <Transition show={isFamilyTreeNameDropdownOpen}>
                                                            <div
                                                                className={`bg-white absolute top-0.5 left-0 right-0 z-10 max-h-[15vh]
                                                                origin-top-right rounded-md shadow-2xl 
                                                                border border-gray-700 overflow-y-scroll
                                                                data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                data-[enter]:duration-100 data-[enter]:ease-out 
                                                                data-[leave]:duration-75 data-[leave]:ease-in`}
                                                            >
                                                                {
                                                                    filteredFamilyTreeNames.length > 0
                                                                        ? filteredFamilyTreeNames.map((familyTreeName, index) => (
                                                                            <button
                                                                                key={index}
                                                                                className={`custom-input-dropdown-list-button text-wrap flex-wrap`}
                                                                                onMouseDown={() => setIsSelectingFamilyTreeName(true)}
                                                                                onClick={() => handleFamilyTreeNameSelect(familyTreeName)}
                                                                            >
                                                                                <p
                                                                                    className={`"text-left text-nowrap !font-custom-semi-bold
                                                                                        overflow-x-scroll no-scrollbar`}
                                                                                >
                                                                                    {familyTreeName}
                                                                                </p>
                                                                            </button>
                                                                        )) : (
                                                                            <button
                                                                                className={`custom-input-dropdown-list-button text-wrap flex-wrap`}
                                                                                style={{userSelect: "none"}}
                                                                            >
                                                                                <p
                                                                                    className="text-left text-nowrap !font-custom-semi-bold
                                                                                            overflow-x-scroll no-scrollbar "
                                                                                >
                                                                                    {t("SEARCH_PAGE.FILTERS.FAMILY_TREE_NAME.NO_RESULTS_FOUND")}
                                                                                </p>
                                                                            </button>
                                                                        )
                                                                }
                                                            </div>
                                                        </Transition>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Un-Married | Side of Family | 75+ / 100+ Age */}
                                            <div className="custom-input-container pb-[2vh] px-[1.25vw]">
                                                {/* Label */}
                                                <label>
                                                    {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.LABEL")}
                                                </label>

                                                {/* Quick Access Options */}
                                                <div className={"custom-input-multiple-fields-container !flex-col"}>
                                                    {/* Unmarried Men | Unmarried Women */}
                                                    <div className="custom-input-multiple-fields-container w-full flex-1">
                                                        {/* Unmarried Men */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isUnmarriedMenSelected ?
                                                                    'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    className="absolute hidden"
                                                                    checked={isUnmarriedMenSelected}
                                                                    onChange={handleUnmarriedMenSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.UNMARRIED_MEN")}
                                                            </p>
                                                        </label>

                                                        {/* Unmarried Women */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isUnmarriedWomenSelected
                                                                    ? 'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isUnmarriedWomenSelected}
                                                                    className="absolute hidden"
                                                                    onChange={handleUnmarriedWomenSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.UNMARRIED_WOMEN")}
                                                            </p>
                                                        </label>
                                                    </div>

                                                    {/* SasurVashin | MaherVashin | Adopted */}
                                                    <div className="custom-input-multiple-fields-container w-full flex-1">
                                                        {/* SasurVashin */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isSasurVashinSelected
                                                                    ? 'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    className="absolute hidden"
                                                                    checked={isSasurVashinSelected}
                                                                    onChange={handleSasurVashinSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.SASURVASHIN")}
                                                            </p>
                                                        </label>

                                                        {/* MaherVashin */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isMaherVashinSelected
                                                                    ? 'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    className={"w-full aspect-square"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isMaherVashinSelected}
                                                                    className="absolute hidden"
                                                                    onChange={handleMaherVashinSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.MAHERVASHIN")}
                                                            </p>
                                                        </label>

                                                        {/* Adopted */}
                                                        {/*<label*/}
                                                        {/*    className="cursor-pointer w-fit flex items-center gap-2*/}
                                                        {/*        bg-white p-[1vh] rounded-xl shadow-lg border-2 border-[#EAEAEA]"*/}
                                                        {/*>*/}
                                                        {/*    <div*/}
                                                        {/*        className={`h-[2.75vh] w-[2.75vh] rounded-md p-[0.5vh]*/}
                                                        {/*        border-2 shadow-md shadow-black/10*/}
                                                        {/*        ${isAdoptedSelected*/}
                                                        {/*            ? 'bg-[#FF6600] border-orange-300'*/}
                                                        {/*            : 'bg-white border-[#CBCBCB]'}`}*/}
                                                        {/*    >*/}
                                                        {/*        <img*/}
                                                        {/*            src={"/assets/tick-icon-light.svg"}*/}
                                                        {/*            className={"w-full aspect-square"}*/}
                                                        {/*            alt={"Select"}*/}
                                                        {/*            title={"Select"}*/}
                                                        {/*        />*/}
                                                        {/*        <input*/}
                                                        {/*            type="checkbox"*/}
                                                        {/*            className="absolute hidden"*/}
                                                        {/*            onChange={(e) => setIsAdoptedSelected(e.target.checked)}*/}
                                                        {/*        />*/}
                                                        {/*    </div>*/}
                                                        {/*    <span className="text-[1.75vh] font-custom-semi-bold text-[#292D32]">*/}
                                                        {/*    {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.ADOPTED")}*/}
                                                        {/*</span>*/}
                                                        {/*</label>*/}
                                                    </div>

                                                    {/* ShatAyushi Vyakti | DirghAyushi Vyakti */}
                                                    <div
                                                        className="custom-input-multiple-fields-container !flex-col w-full flex-1"
                                                    >
                                                        {/* ShatAyushi Vyakti */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isShatayushiVyaktiSelected
                                                                    ? 'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    className="absolute hidden"
                                                                    checked={isShatayushiVyaktiSelected}
                                                                    onChange={handleShatayushiVyaktiSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.SHATAYUSHI_VYAKTI")}
                                                            </p>
                                                        </label>

                                                        {/* DirghAyushi Vyakti */}
                                                        <label className="custom-input-checkbox">
                                                            <div
                                                                className={`${isDirghayushiVyaktiSelected
                                                                    ? 'bg-[#FF6600] border-orange-300'
                                                                    : 'bg-white border-[#CBCBCB]'}`}
                                                            >
                                                                <img
                                                                    src={"/assets/tick-icon-light.svg"}
                                                                    alt={"Select"}
                                                                    title={"Select"}
                                                                />
                                                                <input
                                                                    type="checkbox"
                                                                    className="absolute hidden"
                                                                    checked={isDirghayushiVyaktiSelected}
                                                                    onChange={handleDirghayushiVyaktiSelect}
                                                                />
                                                            </div>
                                                            <p>
                                                                {t("SEARCH_PAGE.FILTERS.QUICK_ACCESS.DIRGHAYUSHI_VYAKTI")}
                                                            </p>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div
                                        style={{boxShadow: "0px 0px 28.3px rgba(255, 102, 0, 0.1)"}}
                                        className={"p-[1.25vw] pt-0 pb-[1.5vh] max-h-fit"}
                                    >
                                        {/* Divider */}
                                        <div className={"h-[0.1vh] w-full bg-[#ECECEC] rounded-full"}/>

                                        {/* Show People (Living | Deceased) */}
                                        <div className="custom-input-container mt-[1.5vh] mb-[2.25vh]">
                                            {/* Label */}
                                            <label className="text-md font-custom-semi-bold text-[#292D32]">
                                                {t("SEARCH_PAGE.FILTERS.FOOTER.SHOW_PEOPLE.LABEL")}
                                            </label>

                                            {/* All People (Living & Deceased) | Living People Only  */}
                                            <div className="custom-input-multiple-fields-container w-full flex-1">
                                                {/* All People (Living & Deceased) */}
                                                <label className="custom-input-checkbox">
                                                    <div
                                                        className={`${showAllPeople
                                                            ? 'bg-[#FF6600] border-orange-300'
                                                            : 'bg-white border-[#CBCBCB]'}`}
                                                    >
                                                        <img
                                                            src={"/assets/tick-icon-light.svg"}
                                                            alt={"Select"}
                                                            title={"Select"}
                                                        />
                                                        <input
                                                            type="checkbox"
                                                            checked={showAllPeople}
                                                            className="absolute hidden"
                                                            onChange={() => setShowAllPeople(true)}
                                                        />
                                                    </div>
                                                    <p>
                                                        {t("SEARCH_PAGE.FILTERS.FOOTER.SHOW_PEOPLE.ALL")}
                                                    </p>
                                                </label>

                                                {/* Living People Only */}
                                                <label className="custom-input-checkbox !flex-none">
                                                    <div
                                                        className={`${!showAllPeople
                                                            ? 'bg-[#FF6600] border-orange-300'
                                                            : 'bg-white border-[#CBCBCB]'}`}
                                                    >
                                                        <img
                                                            src={"/assets/tick-icon-light.svg"}
                                                            alt={"Select"}
                                                            title={"Select"}
                                                        />
                                                        <input
                                                            type="checkbox"
                                                            checked={!showAllPeople}
                                                            className="absolute hidden"
                                                            onChange={() => setShowAllPeople(false)}
                                                        />
                                                    </div>
                                                    <p>
                                                        {t("SEARCH_PAGE.FILTERS.FOOTER.SHOW_PEOPLE.LIVING")}
                                                    </p>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Divider */}
                                        <div className={"h-[0.1vh] w-full bg-[#ECECEC] rounded-full mb-[1.5vh]"}/>

                                        {/* Clear All Filters | Search Buttons */}
                                        <div className={"flex justify-between items-center"}>
                                            {/* Clear All Filters */}
                                            <button
                                                disabled={isClearFiltersButtonDisabled}
                                                className={`flex items-center gap-2 h-[5vh] w-fit
                                                    p-[1vh] pr-[1.25vh] rounded-xl 
                                                    ${isClearFiltersButtonDisabled
                                                    ? 'bg-[#F2F2F2] cursor-default'
                                                    : 'bg-white hover:shadow-lg  hover:shadow-black/15'}
                                                    border-2 border-[#EAEAEA]
                                                    transition duration-300 ease-out`}
                                                onClick={handleClearFilters}
                                            >
                                                <div
                                                    className={`h-full aspect-square rounded-md
                                                        ${isClearFiltersButtonDisabled
                                                        ? 'bg-[#F2F2F2] cursor-default'
                                                        : 'bg-white shadow-md shadow-black/10'} 
                                                        border-2 border-[#CBCBCB]`}
                                                >
                                                    <img
                                                        src={"/assets/close-icon-light-grey.svg"}
                                                        className={"w-full aspect-square"}
                                                        alt={"Clear"}
                                                        title={"Clear"}
                                                    />
                                                </div>

                                                <p className={"font-custom-semi-bold text-[1.75vh]"}>
                                                    {t("SEARCH_PAGE.FILTERS.FOOTER.CLEAR_FILTERS")}
                                                </p>
                                            </button>

                                            {/* Search Button */}
                                            <button
                                                disabled={isApplyFiltersButtonDisabled}
                                                className={`flex items-center h-[5vh] w-fit
                                                    py-[1vh] pl-[1.75vh] pr-[1.25vh] rounded-xl
                                                    ${isApplyFiltersButtonDisabled
                                                    ? 'bg-[#636B75] cursor-default'
                                                    : 'bg-[#292D32] arrow-button hover:shadow-lg  hover:shadow-orange-600/50'}
                                                    border-2 border-[#FFFFFF]/25
                                                    transition duration-300 ease-out`}
                                                onClick={handleApplyFilters}
                                            >
                                                <p className={"font-custom-semi-bold text-white text-[1.75vh]"}>
                                                    {t("SEARCH_PAGE.FILTERS.FOOTER.APPLY_FILTERS")}
                                                </p>
                                                <svg
                                                    viewBox="0 0 6 6"
                                                    fill="none"
                                                    id={"arrow-icon"}
                                                    className={"h-[1vw] w-[1vw] ml-[0.75vw]"}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M3 1L5 3L3 5"
                                                        id={"arrow-icon-tip"}
                                                        stroke="white"
                                                        strokeLinecap="square"
                                                    />
                                                    <path
                                                        d="M1 3L4 3"
                                                        id={"arrow-icon-line"}
                                                        stroke="white"
                                                        strokeLinecap="square"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* Search Bar | Results Container */}
                        <div className={"w-full flex-1 flex overflow-hidden relative rounded-lg shadow-xl"}>
                            <div
                                className="flex-1 bg-white relative
                                    flex flex-col overflow-x-hidden overflow-y-scroll

                                    portrait:p-[0.5rem] portrait:md:p-[0.75rem] portrait:jk:p-[0.85rem] portrait:lg:p-[1rem]
                                    portrait:pr-[0.45rem] portrait:md:pr-[0.7rem] portrait:jk:pr-[0.8rem] portrait:lg:pr-[0.95rem]

                                    landscape:p-[0.75rem] landscape:jk:p-[0.85rem] landscape:lg:p-[1rem]
                                    landscape:pr-[0.7rem] landscape:jk:pr-[0.8rem] landscape:lg:pr-[0.95rem]"
                            >
                                {/* Search Bar | Search Count */}
                                <div
                                    className="flex flex-col gap-[0.5vh] z-20
                                        sticky top-0 bg-white/90"
                                >
                                    {/* Search Bar | Filters Button Container */}
                                    <div className={"custom-input-multiple-fields-container !flex-row"}>
                                        {/* Search Bar */}
                                        <div
                                            className={`custom-input-w-trailing-icon !flex-1 !w-full`}
                                        >
                                            <div
                                                className={`flex items-center justify-center rounded-sm bg-[#F5F5F5]`}
                                            >
                                                <img
                                                    className="!h-fit !w-[75%] aspect-square"
                                                    src="/assets/advanced-user-search-icon.svg"
                                                    alt="Search"
                                                />
                                            </div>

                                            <input
                                                ref={searchInputRef}
                                                id="searchQueryInput"
                                                name="Search Query"
                                                placeholder={t("SEARCH_PAGE.SEARCH_PLACEHOLDER")}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />

                                            {/* Clear Button | Loading Spinner */}
                                            {
                                                isSearchResultsLoading
                                                    ? (
                                                        <Spinner
                                                            visible={isSearchResultsLoading}
                                                            width="2.5vh"
                                                            strokeWidth="5"
                                                            strokeColor={"#FF6600"}
                                                            animationDuration="1"
                                                        />
                                                    )
                                                    : (
                                                        searchQuery && (
                                                            <button
                                                                onClick={handleSearchQueryClearButtonClick}
                                                                className="flex items-center justify-center rounded-md
                                                                    hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                                    transition duration-300 ease-out"
                                                            >
                                                                <img
                                                                    className="h-full w-full"
                                                                    src="/assets/close-icon.png"
                                                                    alt="Clear Search"
                                                                />
                                                            </button>
                                                        )
                                                    )
                                            }
                                        </div>

                                        {/* Filters Button for Portrait Mode */}
                                        {
                                            isAuthenticated && (
                                                <FlyoutFiltersSection
                                                    isClearFiltersButtonDisabled={isClearFiltersButtonDisabled}
                                                    setIsClearFiltersButtonDisabled={setIsClearFiltersButtonDisabled}
                                                    isApplyFiltersButtonDisabled={isApplyFiltersButtonDisabled}
                                                    setIsApplyFiltersButtonDisabled={setIsApplyFiltersButtonDisabled}
                                                    isUpdatingFilters={isUpdatingFilters}
                                                    setIsUpdatingFilters={setIsUpdatingFilters}
                                                    selectedFilters={selectedFilters}
                                                    setSelectedFilters={setSelectedFilters}
                                                    debouncedSearchQuery={debouncedSearchQuery}
                                                    searchResults={searchResults}
                                                    setSearchResults={setSearchResults}
                                                    cachedSearchResults={cachedSearchResults}
                                                    setCachedSearchResults={setCachedSearchResults}
                                                    filteredSearchResults={filteredSearchResults}
                                                    setFilteredSearchResults={setFilteredSearchResults}
                                                    isSearchResultsLoading={isSearchResultsLoading}
                                                    setIsSearchResultsLoading={setIsSearchResultsLoading}
                                                    searchResultsCount={searchResultsCount}
                                                    setSearchResultsCount={setSearchResultsCount}
                                                />
                                            )
                                        }

                                        {/* Export as Excel */}
                                        {
                                            isAuthenticated && isAdmin && (
                                                <button
                                                    disabled={isExportAsExcelDisabled}
                                                    className={`custom-button-w-trailing-icon h-full !flex-[0] 
                                                        portrait:aspect-square portrait:items-center portrait:!justify-center
                                                        ${isExportAsExcelDisabled ? 'bg-[#F2F2F2] cursor-default'
                                                        : 'bg-white hover:shadow-lg hover:shadow-black/20'}
                                                        transition-all duration-150 ease-in-out`}
                                                    onClick={exportAsExcel}
                                                >
                                                    <div className={"portrait:!hidden"}>
                                                        <p className={"whitespace-nowrap !font-custom-semi-bold"}>
                                                            {`${t("SU_DASHBOARD.USERS.DOWNLOAD.LABEL")} ${t("SU_DASHBOARD.USERS.DOWNLOAD.OPTIONS.EXCEL")}`}
                                                        </p>
                                                    </div>
                                                    {
                                                        isExportAsExcelLoading
                                                            ? (
                                                                <Spinner
                                                                    visible={isExportAsExcelLoading}
                                                                    width="3vh"
                                                                    strokeWidth="5"
                                                                    strokeColor={"#FF6600"}
                                                                    animationDuration="4"
                                                                />
                                                            )
                                                            : (
                                                                <DownloadIcon
                                                                    aria-hidden="true"
                                                                    stroke={"#292D32"}
                                                                    strokeWidth={"0.1vh"}
                                                                />
                                                            )
                                                    }
                                                </button>
                                            )
                                        }
                                    </div>

                                    {/* Search Results Count */}
                                    <div
                                        className="mt-[0.5vh] mb-[0.75vh] leading-tight text-[1.5vh] font-custom-mono-medium-italic"
                                    >
                                        <p>
                                            {
                                                searchResultsCount && (
                                                    filteredSearchResults.length === 100
                                                        ? `Found ${searchResultsCount} results, showing first 100 results...`
                                                        : `Found ${filteredSearchResults.length} results...`
                                                )
                                            }
                                        </p>
                                    </div>
                                </div>

                                {
                                    searchQuery === "" && Object.keys(selectedFilters).length === 0 && !isUpdatingFilters
                                        ? (
                                            <div
                                                className="p-[1vw] flex-1 flex flex-col
                                                    items-center justify-center h-full gap-[2vh]"
                                            >
                                                <img
                                                    src="/assets/ganeshji-logo.png"
                                                    className="portrait:h-[20vh] landscape:w-[12.5vw] aspect-auto"
                                                    alt="GaneshJi"
                                                    title="GaneshJi"
                                                />

                                                <p
                                                    className="text-center portrait:text-[2vh] landscape:text-[3vh]
                                                        font-custom-mono-semi-bold-italic text-[#292D32]"
                                                >
                                                    {
                                                        isAuthenticated
                                                            ? t("SEARCH_PAGE.SELECT_FILTERS_TO_CONTINUE")
                                                            : t("SEARCH_PAGE.ENTER_NAME_TO_CONTINUE")
                                                    }
                                                </p>
                                            </div>
                                        ) : (
                                            filteredSearchResults.length > 0
                                                ? (
                                                    <div
                                                        className={`pt-[1.25vh] pb-[2vh] gap-4
                                                            grid portrait:grid-cols-1 
                                                            ${!isAuthenticated
                                                            ? 'landscape:md:grid-cols-4'
                                                            : 'landscape:md:grid-cols-3'}
                                                            justify-between`}
                                                    >
                                                        {
                                                            filteredSearchResults.map((result, index) => (
                                                                <button
                                                                    onClick={() => {
                                                                        if (isAuthenticated) {
                                                                            window.open(result._id === account._id
                                                                                ? `/personal-profile`
                                                                                : `/public-profile/${result._id}`, '_blank');
                                                                        } else {
                                                                            setSelectedProfileDetails(result);
                                                                            setTimeout(() => {
                                                                                setShowSelectedProfileDetailsModal(true);
                                                                            }, 100);
                                                                            // toast.info("Coming Soon!");
                                                                        }
                                                                    }}
                                                                    key={index}
                                                                    className="w-full
                                                                        min-h-[12.5vh] max-h-[12.5vh] col-span-1
                                                                        flex-1 flex items-center gap-[1.5vh]
                                                                        p-[1.25vh] border-2 border-[#EAEAEA]
                                                                        rounded-2xl bg-white"
                                                                    style={{boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)"}}
                                                                >
                                                                    <div
                                                                        className={`shadow-xl z-10 h-full aspect-square rounded-xl
                                                                        flex items-center justify-center ${result.imageUrlBg}
                                                                        border-2 border-white overflow-hidden`}
                                                                    >
                                                                        {
                                                                            result.imageUrl &&
                                                                            result.imageUrl.length <= 2
                                                                                ? (
                                                                                    <p className={"text-[3vh] text-white font-custom-mono-bold"}>
                                                                                        {result.imageUrl.toUpperCase()}
                                                                                    </p>

                                                                                ) : (
                                                                                    <img
                                                                                        className="object-cover h-full drop-shadow-2xl aspect-square"
                                                                                        src={result.imageUrl}
                                                                                        alt="User Profile Image"
                                                                                        referrerPolicy={"no-referrer"}
                                                                                    />
                                                                                )
                                                                        }
                                                                    </div>
                                                                    <div
                                                                        className="h-full
                                                                            flex-1 flex flex-col
                                                                            items-start justify-between
                                                                            overflow-scroll no-scrollbar"
                                                                    >
                                                                        <div
                                                                            className={"flex flex-col gap-[0.5vh] w-full items-start"}
                                                                        >
                                                                            <p
                                                                                className="line-clamp-1 text-ellipsis overflow-scroll no-scrollbar
                                                                                    leading-tight text-[2vh] font-custom-bold text-[#3D3D3D]"
                                                                            >
                                                                                {`${result.firstName} ${result.middleName}`}
                                                                            </p>
                                                                            <p className="leading-tight text-[2vh] font-custom-black text-[#3D3D3D]">
                                                                                {`${result.firstNameDevanagariScript} ${result.middleNameDevanagariScript}`}
                                                                            </p>
                                                                        </div>
                                                                        <div
                                                                            className="h-fit max-w-full flex items-center justify-between
                                                                            overflow-hidden bg-[#F3BC50] px-[1vw] py-[0.5vh]
                                                                            rounded-full overflow-x-scroll no-scrollbar
                                                                            text-nowrap line-clamp-1"
                                                                        >
                                                                            <p className={"text-ellipsis text-[1.35vh] font-custom-mono-bold text-[#3D3D3D]"}>
                                                                                {result.familyTreeName}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                    </div>
                                                )
                                                : (
                                                    <div
                                                        className="p-[1vw] flex-1 flex flex-col
                                                        items-center justify-center h-full gap-[2vh]"
                                                    >
                                                        <img
                                                            src="/assets/ganeshji-logo.png"
                                                            className="portrait:h-[20vh] landscape:w-[12.5vw] aspect-auto"
                                                            alt="GaneshJi"
                                                            title="GaneshJi"
                                                        />
                                                        {
                                                            filteredSearchResults.length === 0 && !isSearchResultsLoading && (
                                                                <p
                                                                    className="text-center portrait:text-[2vh] landscape:text-[3vh]
                                                                    font-custom-mono-semi-bold-italic text-[#292D32]"
                                                                >
                                                                    {t("SEARCH_PAGE.NO_RESULTS_FOUND")}
                                                                </p>
                                                            )
                                                        }
                                                    </div>
                                                )
                                        )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                !isAuthenticated && (
                    <Modal isOpen={showSelectedProfileDetailsModal}>
                        <div
                            className={`form-modal-container portrait:!rounded-lg
                                portrait:!min-h-fit portrait:!h-fit portrait:!max-h-fit
                                portrait:!min-w-[80vw] portrait:!w-[80vw] portrait:!max-w-[80vw]
                                portrait:md:!min-w-[36vw] portrait:md:!w-[36vw] portrait:md:!max-w-[36vw]
                                portrait:lg:!min-w-[30vw] portrait:lg:!w-[30vw] portrait:lg:!max-w-[30vw]

                                landscape:!min-w-[27.5vw] landscape:!w-[27.5vw] landscape:!max-w-[27.5vw]
                                landscape:lg:!min-w-[30vw] landscape:lg:!w-[30vw] landscape:lg:!max-w-[30vw]`}
                        >
                            {/* Header */}
                            <div className={"form-modal-header-container"}>
                                {/* Header Title | Close Button */}
                                <div className={"flex flex-row items-center justify-between"}>
                                    {/* Form Title */}
                                    <h2>
                                        {t("SEARCH_PAGE.SELECTED_PROFILE_MODAL.TITLE")}
                                    </h2>

                                    {/* Close Button */}
                                    <button onClick={handleClose}>
                                        <img
                                            className={"w-[75%] h-[75%]"}
                                            src={"/assets/close-icon.png"}
                                            alt={"Close"}
                                            title={"Close"}
                                        />
                                    </button>
                                </div>

                                {/* Divider */}
                                <div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>
                            </div>

                            {/* Body */}
                            <div className={"form-modal-body-container"}>
                                {/* PFP | Gender | Name | Family Tree Name */}
                                <div
                                    className={`overflow-x-clip gap-y-2 flex 
                                        flex-col items-center justify-between`}
                                >
                                    {/* PFP */}
                                    <div
                                        className={`relative flex items-center justify-center 
                                            shadow-xl rounded-full z-10 aspect-square
                                            portrait:h-[10rem] portrait:jk:h-[12.5rem] portrait:lg:h-[15rem]
                                            landscape:h-[6.5rem] landscape:jk:h-[9rem] landscape:lg:h-[12.5rem]
                                            ${selectedProfileDetails?.imageUrlBg} border-4 border-white`}
                                    >
                                        {
                                            selectedProfileDetails?.imageUrl &&
                                            selectedProfileDetails?.imageUrl.length <= 2
                                                ? (
                                                    <p
                                                        className="text-white font-custom-mono-bold
                                                            portrait:text-[3rem] portrait:jk:text-[3.75rem] portrait:lg:text-[4.5rem]
                                                            landscape:text-[1.75rem] landscape:jk:text-[2.25rem] landscape:lg:text-[3rem]"
                                                    >
                                                        {selectedProfileDetails?.imageUrl.toUpperCase()}
                                                    </p>

                                                ) : (
                                                    <img
                                                        className="object-cover drop-shadow-2xl h-full w-full rounded-full"
                                                        src={selectedProfileDetails?.imageUrl}
                                                        alt="User Profile Image"
                                                        referrerPolicy={"no-referrer"}
                                                    />
                                                )
                                        }
                                    </div>

                                    {/* Basic Details */}
                                    <div className={"w-full flex flex-col items-center justify-between gap-1"}>
                                        {/* Gender | Name */}
                                        <div className={"flex items-center justify-center mt-[1vh] gap-2"}>
                                            <div className={"w-full h-full flex-1 flex items-center justify-end"}>
                                                <img
                                                    className="aspect-square
                                                        portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                                        landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
                                                    src={
                                                        selectedProfileDetails?.gender === "Male"
                                                            ? "/assets/male-gender-icon-bold.png"
                                                            : "/assets/female-gender-icon-bold.png"
                                                    }
                                                    alt={selectedProfileDetails?.gender === "Male" ? "Male" : "Female"}
                                                />
                                            </div>
                                            <p
                                                className="flex-1 text-center font-custom-bold leading-tight
                                                    portrait:text-[1.5rem] portrait:jk:text-[1.75rem] portrait:lg:text-[2rem]
                                                    landscape:text-[1rem] landscape:jk:text-[1.25rem] landscape:lg:text-[1.5rem]"
                                            >
                                                {selectedProfileDetails?.firstName + ' ' + selectedProfileDetails?.middleName}
                                            </p>
                                            <div className={"w-full h-full flex-1 flex items-center justify-start"}>
                                                <div
                                                    className="aspect-square bg-transparent
                                                        portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                                        landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
                                                />
                                            </div>
                                        </div>

                                        {/* Family Tree Name */}
                                        <p
                                            className="w-full text-center text-[#FF6600] font-custom-semi-bold
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
                                        >
                                            {
                                                '(' +
                                                (locale === "en"
                                                    ? selectedProfileDetails?.familyTreeName
                                                    : selectedProfileDetails?.familyTreeNameDevanagariScript) + ')'
                                            }
                                        </p>
                                    </div>
                                </div>

                                {/* Basic Details */}
                                <div
                                    className={`gap-1 flex flex-col items-start`}
                                >
                                    {/*/!* Divider - Header *!/*/}
                                    {/*<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mt-[1.5vh]"}/>*/}

                                    {/* Date Of Birth */}
                                    <div className={"w-full flex items-center justify-center gap-1"}>
                                        <p
                                            className="text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.6rem] landscape:jk:text-[0.75rem] landscape:lg:text-[1rem]"
                                        >
                                            {t("SEARCH_PAGE.SELECTED_PROFILE_MODAL.DOB")}
                                        </p>
                                        <p
                                            className="text-[#6D6D6D] font-custom-medium
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.6rem] landscape:jk:text-[0.75rem] landscape:lg:text-[1rem]"
                                        >
                                            {formatBirthDate(selectedProfileDetails?.dateOfBirth)}
                                        </p>
                                    </div>

                                    {/* City */}
                                    <div className={"w-full flex items-center justify-center gap-1"}>
                                        <p
                                            className="text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.6rem] landscape:jk:text-[0.75rem] landscape:lg:text-[1rem]"
                                        >
                                            {t("SEARCH_PAGE.SELECTED_PROFILE_MODAL.CITY")}
                                        </p>
                                        <p
                                            className={`font-custom-medium leading-tight
                                                ${selectedProfileDetails?.address?.city ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.6rem] landscape:jk:text-[0.75rem] landscape:lg:text-[1rem]`}
                                            style={{userSelect: selectedProfileDetails?.address?.city ? "auto" : "none"}}
                                        >
                                            {/*
                                                , ${selectedProfileDetails?.address?.state},
                                                        ${selectedProfileDetails?.address?.country} - ${selectedProfileDetails?.address?.pincode}
                                            */}
                                            {
                                                selectedProfileDetails?.address?.city
                                                    ? `${selectedProfileDetails?.address?.city}`
                                                    : 'Address'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={"form-modal-footer-container"}>
                                {/* Divider */}
                                <div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>

                                {/* Contact Button Container */}
                                <div className="flex justify-end">
                                    {/* Submit Button */}
                                    <button
                                        onClick={handleContact}
                                        className="form-modal-footer-container-submit-button"
                                    >
                                        <p>
                                            {t("SEARCH_PAGE.SELECTED_PROFILE_MODAL.CONTACT")}
                                        </p>
                                        <svg
                                            viewBox="0 0 6 6"
                                            fill="none"
                                            id={"arrow-icon"}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M3 1L5 3L3 5"
                                                id={"arrow-icon-tip"}
                                                stroke="black"
                                                strokeLinecap="square"
                                            />
                                            <path
                                                d="M1 3L4 3"
                                                id={"arrow-icon-line"}
                                                stroke="black"
                                                strokeLinecap="square"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )
            }
        </>
    );
};
